import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./EmployeeView.css";
import EmployeeViewManager from "../managers/EmployeeViewManager";
import { EmployeeInfo, RoleInfo } from "../interfaces/EmployeeInfoInterface";
import { useSiteContext } from "../SiteContext";
import EmployeeSyncModalV2 from "../components/EmployeeSyncModalV2";
import EnrollmentPhotoModal, {
    EnrollmentChangeAction,
    EnrollmentModalEmployee,
} from "../components/enrollment/EnrollmentPhotoModal";
import { EnmSpin } from "../components/enrollment/EnrollmentChip";
import EmployeesNeedingPhotosModal from "../components/EmployeesNeedingPhotosModal";
import {
    enrollmentState,
    formatPhotoDate,
    MissingPhotoRow,
    orderNeedingPhotos,
    PHOTO_SORT_RANK,
    PHOTO_STATE_LABEL,
} from "../util/photoCoverage";
import { enrollmentReason, enrollmentStatusText, fmtTime, fmtWhen } from "../util/enrollmentReasons";
import type { EmployeeEnrollment, EnrollmentState } from "../interfaces/SelfieEnrollment";
import { consumeStashedDeepLink, peekStashedDeepLink } from "../interfaces/DeepLinkInterface";
import EmployeeRoleEditModal from "../components/EmployeeRoleEditModal";
import EmployeePWClassificationModal from "../components/EmployeePWClassificationModal";
import EmployeeWorkOrderRateModal from "../components/EmployeeWorkOrderRateModal";
import TimeOffBalancesTable, { fmtDays, fmtHrs } from "../components/TimeOffBalancesTable";
import { applyOffsetToUTCDateTime } from "../util/DateAndTimeUtil";
import { can } from "../util/adminPermissions";
import { isUnpaid } from "../util/timeOffUnpaid";
import { duplicateSortKey, findDuplicatePhones, normalizePhone } from "../util/duplicatePhones";
import config from "../configLoader";
import { sortSupervisorOptions } from "../utils/supervisorPickerOptions";
import FloatMenuButton, { FloatMenuOption } from "../components/v2/FloatMenuButton";
import {
    getEmployeeTimeOffPolicy,
    assignEmployeeTimeOffPolicy,
    getTimeOffPolicies,
    getEmployeeTimeOffBalance,
    getEmployeeTimeOffLedger,
} from "../api/timeOff";
import type {
    EmployeeTimeOffPolicyItem,
    TimeOffPolicySummary,
    EmployeeTimeOffBalanceRow,
    TimeOffLedgerEntry,
} from "../interfaces/TimeOff";
import {
    Users,
    Search,
    ChevronRight,
    ChevronLeft,
    ChevronUp,
    ChevronDown,
    Camera,
    RefreshCw,
    Truck,
    ClipboardList,
    Info,
    Check,
    CheckCircle,
    AlertTriangle,
    X,
    Layers,
    Calendar,
    Clock,
    UserX,
    UserCheck,
    PhoneCall,
} from "lucide-react";

// ─── Sort keys ──────────────────────────────────────────────────────────────
type SortKey =
    | "Name"
    | "ID"
    | "Phone"
    | "JobTitle"
    | "Role"
    | "LastLogin"
    | "Supervisor"
    | "PWClassification"
    | "DriveTime"
    | "CompanyPhone"
    | "WorkOrders"
    | "WORate"
    | "TimeOffPolicy"
    | "Photo";

const SORT_LABELS: Record<SortKey, string> = {
    Name: "Name",
    ID: "ID",
    Phone: "Cell Phone",
    JobTitle: "Job Title",
    Role: "Role",
    LastLogin: "Last Login",
    Supervisor: "Supervisor",
    PWClassification: "PW Classification",
    DriveTime: "Drive times",
    CompanyPhone: "Company phone",
    WorkOrders: "Work orders",
    WORate: "WO Rate",
    TimeOffPolicy: "Time Off Policy",
    Photo: "Photo status",
};

const TO_HELP = "The accrual policy that governs what time off this employee earns and can request in the mobile app.";

const DASH = "—";
const PAGE_SIZES = [15, 30, 50, 100];
const DRIVE_HELP =
    "Controls whether this employee can log drive time at the beginning and end of the day in the mobile app.";
const PHONE_HELP =
    "Whether this employee has a company phone. Employees with a company phone do not receive phone reimbursement.";
const WO_HELP = "Controls whether this employee can create Work Orders in the mobile app.";
const WO_RATE_HELP =
    "The hourly rate paid for Work Order claims. Defaults to the employee's system (PAN) rate unless an override is set.";
const PHOTO_HELP =
    "Enrollment status. Green check = verified by Azure for face-match clock-in. Spinner = still being checked. Red = Azure rejected the photo. Amber = no photo yet.";
/** The roster poll while any row in scope is verifying. GET /admin/employees is the big query — never faster. */
const PHOTO_POLL_MS = 60_000;
type PhotoFilter = "all" | EnrollmentState;
/** Callout count chips, in order. Each one is the Photo filter. */
const PHOTO_COUNT_CHIPS: [EnrollmentState, string][] = [
    ["none", "need a photo"],
    ["failed", "failed enrollment"],
    ["verifying", "verifying"],
];
/** Avatar cue: amber for no photo, red for failed; verifying gets none (nothing to act on). */
const AVATAR_CUE: Record<EnrollmentState, string> = { none: " nophoto", failed: " enfail", verifying: "", enrolled: "" };

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtISO(iso: string | null | undefined): string {
    if (!iso) return DASH;
    const p = iso.split("-");
    if (p.length !== 3) return iso;
    return `${MON[(parseInt(p[1], 10) || 1) - 1]} ${parseInt(p[2], 10) || 1}, ${p[0]}`;
}
function todayISO(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Hours formatting ("36 h" / "4.5 days" on an 8h workday) lives with the balances table now,
// so the Balances and Activity panes can't drift apart.
/** Ledger dates arrive as ISO (possibly datetime) — display the date part. */
function fmtLedgerDate(iso: string): string {
    return fmtISO(iso ? iso.slice(0, 10) : iso);
}

// ─── Time Off modal tabs ──────────────────────────────────────────────────────
type ToTab = "balances" | "activity" | "assignment";

const LEDGER_PAGE_SIZE = 50;

/** Per-category ledger fetch state, cached while the modal is open. */
interface ToLedgerState {
    loading: boolean;      // initial (page 0) fetch in flight
    loadingMore: boolean;  // next-page fetch in flight
    error: boolean;
    currentBalanceHours: number;
    pending: TimeOffLedgerEntry[];
    items: TimeOffLedgerEntry[];
    total: number;
    nextPage: number;      // next page index to fetch (also the retry target)
}

const emptyLedgerState = (): ToLedgerState => ({
    loading: true,
    loadingMore: false,
    error: false,
    currentBalanceHours: 0,
    pending: [],
    items: [],
    total: 0,
    nextPage: 0,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
function initials(first: string, last: string): string {
    return `${(first || " ")[0]}${(last || " ")[0]}`.toUpperCase();
}

/** Today's local date as YYYY-MM-DD (no UTC shift). */
function todayLocalISO(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** YYYY-MM-DD → MM/DD/YYYY by string parsing (avoids timezone shifting). */
function fmtISODate(iso: string | null | undefined): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
    return m ? `${m[2]}/${m[3]}/${m[1]}` : iso ?? "";
}

/** raw digits → +CC (AAA) NNN-NNNN (falls back to the raw digits if unexpected length). */
function formatPhoneNumber(phoneNumber: string): string | null {
    if (!phoneNumber) return null;
    const digits = phoneNumber.replace(/\D/g, "");
    let cc = "1";
    let rest = digits;
    if (digits.length > 10) {
        cc = digits.slice(0, digits.length - 10);
        rest = digits.slice(-10);
    }
    if (rest.length !== 10) return digits;
    return `+${cc} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
}

const EmployeeView: React.FC = () => {
    const { setLoading, createPopup, locations, userRole, translator, pendingDeepLink } = useSiteContext();

    const canEdit = can(userRole, "editEmployeeRole");
    const canActivate = can(userRole, "activateInactivateEmployees");
    const canManageWORates = can(userRole, "manageWorkOrderRates");
    const showDrive = !!config.showDriveTimeManagement;
    const showPhone = !!config.showCompanyPhone;
    const canManagePhones = can(userRole, "manageCompanyPhones");
    const showWorkOrders = !!config.showWorkOrdersPage;
    const showPW = !!config.showPrevailingWagePages;
    const showTimeOff = !!config.showTimeOffConfig;
    const showPhoto =
        !!config.showPhotoPages &&
        (userRole.admin || !!userRole.viewEmployeeEnrollmentPhotos || !!userRole.editEmployeeEnrollmentPhotos);
    const showLocationFilter = userRole.admin || userRole.payroll || userRole.hr;

    // data
    const [data, setData] = useState<EmployeeInfo[] | null>(null);
    const [employeeRoles, setEmployeeRoles] = useState<RoleInfo[]>([]);

    // filters / controls
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedSuper, setSelectedSuper] = useState<string>("ALL");
    const [selectedLocation, setSelectedLocation] = useState<string>("ALL");
    const [isActive, setIsActive] = useState<boolean>(true);
    const [driveFilter, setDriveFilter] = useState<"all" | "on" | "off">("all");
    const [phoneFilter, setPhoneFilter] = useState<"all" | "on" | "off">("all");
    const [woFilter, setWoFilter] = useState<"all" | "on" | "off">("all");
    const [woRateAuditOnly, setWoRateAuditOnly] = useState<boolean>(false);
    const [timeOffFilter, setTimeOffFilter] = useState<string>("all");
    const [photoFilter, setPhotoFilter] = useState<PhotoFilter>("all");
    // Duplicate phones view: overrides the Active/Inactive toggle and lists both, grouped by number.
    const [dupPhonesOnly, setDupPhonesOnly] = useState<boolean>(false);
    const [sortKey, setSortKey] = useState<SortKey>("Name");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    // pagination
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(30);

    // modals
    const [isSyncModalOpen, setIsSyncModalOpen] = useState<boolean>(false);
    const [enrollmentEmployee, setEnrollmentEmployee] = useState<
        (EnrollmentModalEmployee & { enrollment?: EmployeeEnrollment | null }) | null
    >(null);
    const [roleEditEmployee, setRoleEditEmployee] = useState<EmployeeInfo | null>(null);
    const [pwClassificationModalEmployee, setPWClassificationModalEmployee] = useState<EmployeeInfo | null>(null);
    const [woRateModalEmployee, setWoRateModalEmployee] = useState<EmployeeInfo | null>(null);
    const [needingPhotosOpen, setNeedingPhotosOpen] = useState<boolean>(false);
    // enrollment-photo coverage (TAS-220): callout dismissal is session-only
    const [photoBannerDismissed, setPhotoBannerDismissed] = useState<boolean>(false);
    const [dupBannerDismissed, setDupBannerDismissed] = useState<boolean>(false);
    // enrollment status (TAS-568): when the roster (and so every row's enrollment) was last read
    const [photoStatusAt, setPhotoStatusAt] = useState<Date | null>(null);
    const [refreshingPhotos, setRefreshingPhotos] = useState<boolean>(false);
    const photoDeepLinkConsumed = useRef(false);

    // drive-time flow
    const [driveConfirm, setDriveConfirm] = useState<{ emp: EmployeeInfo; next: boolean } | null>(null);
    const [savingDrive, setSavingDrive] = useState<Set<number>>(new Set());

    // company-phone flow (gated on ManageCompanyPhones)
    const [phoneConfirm, setPhoneConfirm] = useState<{ emp: EmployeeInfo; next: boolean } | null>(null);
    const [phoneDate, setPhoneDate] = useState<string>(todayLocalISO());
    const [savingPhone, setSavingPhone] = useState<Set<number>>(new Set());

    // activate / inactivate flow (gated on ActivateInactivateEmployees)
    const [statusConfirm, setStatusConfirm] = useState<{ emp: EmployeeInfo; next: boolean } | null>(null);

    // work-order-creation flow
    const [woConfirm, setWoConfirm] = useState<{ emp: EmployeeInfo; next: boolean } | null>(null);
    const [savingWO, setSavingWO] = useState<Set<number>>(new Set());
    const [syncingRows, setSyncingRows] = useState<Set<number>>(new Set());

    // time-off policy modal
    const [toEmployee, setToEmployee] = useState<EmployeeInfo | null>(null);
    const [toLoading, setToLoading] = useState<boolean>(false);
    const [toCurrent, setToCurrent] = useState<EmployeeTimeOffPolicyItem | null>(null);
    const [toHistory, setToHistory] = useState<EmployeeTimeOffPolicyItem[]>([]);
    const [toDataFetched, setToDataFetched] = useState<boolean>(false);
    const [toActivePolicies, setToActivePolicies] = useState<TimeOffPolicySummary[]>([]);
    const [toSelectedPolicyID, setToSelectedPolicyID] = useState<number | "">("");
    const [toStartDate, setToStartDate] = useState<string>(todayISO());
    const [toNotes, setToNotes] = useState<string>("");
    const [toDateError, setToDateError] = useState<boolean>(false);
    const [toSaving, setToSaving] = useState<boolean>(false);
    // read-only Balances / Activity tabs
    const [toTab, setToTab] = useState<ToTab>("balances");
    const [toBalances, setToBalances] = useState<EmployeeTimeOffBalanceRow[] | null>(null);
    const [toBalancesLoading, setToBalancesLoading] = useState<boolean>(false);
    const [toBalancesError, setToBalancesError] = useState<boolean>(false);
    const [toSelectedCatID, setToSelectedCatID] = useState<number | null>(null);
    /** timeOffCategoryID → ledger pages fetched so far (reset on modal open). */
    const [toLedgers, setToLedgers] = useState<Record<number, ToLedgerState>>({});
    /** adminEmployeeID → "First Last", lazily fetched once for the "Assigned by" line. */
    const [adminNames, setAdminNames] = useState<Map<number, string> | null>(null);
    const adminNamesFetchedRef = useRef(false);

    // toast
    // `spin`: the slate enrollment spinner replaces the check while a job is still in flight.
    type ToastState = {
        kind: "ok" | "err";
        message: React.ReactNode;
        actionLabel?: string;
        onAction?: () => void;
        spin?: boolean;
        ms?: number;
    };
    const [toast, setToast] = useState<ToastState | null>(null);
    const toastTimer = useRef<number | null>(null);
    const showToast = useCallback((t: ToastState) => {
        setToast(t);
        if (toastTimer.current) window.clearTimeout(toastTimer.current);
        toastTimer.current = window.setTimeout(() => setToast(null), t.ms ?? (t.kind === "err" ? 5200 : 4200));
    }, []);
    useEffect(() => () => { if (toastTimer.current) window.clearTimeout(toastTimer.current); }, []);

    // ─── Data loading ─────────────────────────────────────────────────────────
    // `silent` skips the page-blocking spinner (photo-status poll and the result-line Refresh).
    // Returns the fresh rows, or null on failure.
    const fetchData = useCallback(async (opts?: { silent?: boolean }): Promise<EmployeeInfo[] | null> => {
        if (!opts?.silent) setLoading(true);
        try {
            const response = await EmployeeViewManager.getEmployees();
            setData(response);
            setPhotoStatusAt(new Date());
            return response;
        } catch (error) {
            console.error("Error fetching data:", error);
            return null;
        } finally {
            if (!opts?.silent) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setLoading unstable from SiteContext
    }, []);

    const fetchEmployeeRoles = useCallback(async () => {
        try {
            const roles = await EmployeeViewManager.getEmployeeRoles();
            setEmployeeRoles(roles);
        } catch (error) {
            console.error("Error fetching employee roles:", error);
        }
    }, []);

    useEffect(() => {
        fetchData();
        fetchEmployeeRoles();
    }, [fetchData, fetchEmployeeRoles]);

    // Reset supervisor when the location changes (rebuilds the list for that location).
    useEffect(() => {
        setSelectedSuper("ALL");
    }, [selectedLocation]);

    // Reset to page 1 whenever the result set can change.
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedSuper, selectedLocation, isActive, dupPhonesOnly, driveFilter, phoneFilter, woFilter, timeOffFilter, photoFilter, itemsPerPage]);

    // ─── Lookups ──────────────────────────────────────────────────────────────
    const employeeByID = useMemo(() => {
        const m = new Map<number, EmployeeInfo>();
        (data ?? []).forEach((e) => m.set(e.employeeID, e));
        return m;
    }, [data]);

    const employeeByAltID = useMemo(() => {
        const m = new Map<string, EmployeeInfo>();
        (data ?? []).forEach((e) => { if (e.altEmployeeID) m.set(e.altEmployeeID, e); });
        return m;
    }, [data]);

    const roleByID = useMemo(() => {
        const m = new Map<number, RoleInfo>();
        employeeRoles.forEach((r) => m.set(r.roleID, r));
        return m;
    }, [employeeRoles]);

    // Shared phone numbers across the WHOLE roster (active + inactive), regardless of filters.
    const dupPhones = useMemo(() => findDuplicatePhones(data ?? []), [data]);
    const dupGroupOf = useCallback(
        (e: EmployeeInfo) => {
            const key = normalizePhone(e.phoneNumber);
            return key ? dupPhones.get(key) : undefined;
        },
        [dupPhones]
    );

    const activeEmployeeRoles = useMemo(() => employeeRoles.filter((r) => r.active), [employeeRoles]);

    const getRoleDisplay = useCallback(
        (roleID: number): string => {
            const role = roleByID.get(roleID);
            if (!role) return roleID ? String(roleID) : DASH;
            return `${role.name} (${role.roleID})`;
        },
        [roleByID]
    );

    const getSupervisorName = useCallback(
        (supervisorID: number): string => {
            if (supervisorID === 0) return DASH;
            const sup = employeeByID.get(supervisorID);
            return sup ? `${sup.firstName} ${sup.lastName}` : DASH;
        },
        [employeeByID]
    );

    const getLocationName = useCallback(
        (locationID: number): string => {
            const loc = locations?.find((l) => l.locationID === locationID);
            return loc ? loc.name : DASH;
        },
        [locations]
    );

    const formatPWClassification = (emp: EmployeeInfo): string =>
        emp.pwClassification?.name
            ? `${emp.pwClassification.name}${emp.pwClassification.altPWClassificationID ? ` (${emp.pwClassification.altPWClassificationID})` : ""}`
            : DASH;

    // supervisors = employees referenced as someone's supervisor, filtered to the chosen location
    const supervisors = useMemo((): (EmployeeInfo & { name: string })[] => {
        if (!data) return [];
        const supIDs = new Set(data.map((e) => e.supervisorID));
        // The option text already leads with the EE#; under the EE#-first flag the ordering
        // follows it numerically instead of by first name, so the list reads in order.
        return sortSupervisorOptions(
            data
                .filter((e) => supIDs.has(e.employeeID))
                .filter((e) => selectedLocation === "ALL" || e.locationID === parseInt(selectedLocation, 10))
                .map((e) => ({ ...e, name: `${e.firstName} ${e.lastName}` })),
            (a, b) => a.name.localeCompare(b.name)
        );
    }, [data, selectedLocation]);

    // Supervisor picker options. The label keeps the "altID — First Last" shape this page
    // has always used: it already leads with the EE#, so every tenant can type a number to
    // filter. supervisorOptionLabel is deliberately NOT used here — under a flag-off tenant
    // it would strip the EE# this page shows today. Ordering still follows the flag, via
    // sortSupervisorOptions on `supervisors` above.
    // Option values are altEmployeeID (NOT employeeID) — selectedSuper is matched against
    // that downstream; "0" is the sentinel for top-level supervisors.
    const supervisorMenuOptions: FloatMenuOption[] = useMemo(() => [
        { value: "ALL", label: "All supervisors" },
        { value: "0", label: "0 — Supervisors (top level)" },
        ...supervisors.map((s) => ({
            value: s.altEmployeeID,
            label: `${s.altEmployeeID} — ${s.firstName} ${s.lastName}`,
        })),
    ], [supervisors]);

    // ─── Filter + sort ────────────────────────────────────────────────────────
    // `scopeRows` = every filter EXCEPT the photo filter (the coverage callout counts against it,
    // so the number never collapses to "12 of 12" once you narrow to Needed); `rows` adds it.
    const filtered = useMemo(() => {
        if (!data) return { rows: [] as EmployeeInfo[], scopeRows: [] as EmployeeInfo[] };
        const q = searchTerm.trim();
        const isDigits = /^\d+$/.test(q);
        const hasLetters = /[a-zA-Z]/.test(q);
        const selectedSupEmp =
            selectedSuper !== "ALL" && selectedSuper !== "0" ? employeeByAltID.get(selectedSuper) : undefined;

        const scopeRows = data.filter((e) => {
            if (dupPhonesOnly) {
                if (!dupGroupOf(e)) return false;
            } else if (e.active !== isActive) return false;
            if (selectedLocation !== "ALL" && e.locationID !== parseInt(selectedLocation, 10)) return false;
            if (selectedSuper !== "ALL") {
                if (selectedSuper === "0") {
                    if (e.supervisorID !== 0) return false;
                } else {
                    if (!selectedSupEmp || e.supervisorID !== selectedSupEmp.employeeID) return false;
                }
            }
            if (showDrive && driveFilter !== "all") {
                if (driveFilter === "on" && !e.canAddDriveTime) return false;
                if (driveFilter === "off" && e.canAddDriveTime) return false;
            }
            if (showPhone && phoneFilter !== "all") {
                if (phoneFilter === "on" && !e.hasCompanyPhone) return false;
                if (phoneFilter === "off" && e.hasCompanyPhone) return false;
            }
            if (showWorkOrders && woFilter !== "all") {
                if (woFilter === "on" && !e.canCreateWorkOrders) return false;
                if (woFilter === "off" && e.canCreateWorkOrders) return false;
            }
            if (showWorkOrders && woRateAuditOnly) {
                if (!(e.workOrderRate != null && e.workOrderRate !== e.hourlyRate)) return false;
            }
            if (showTimeOff && timeOffFilter !== "all") {
                if (timeOffFilter === "unassigned") {
                    // Only show rows where field is PRESENT and null (truly Unassigned)
                    if (e.timeOffPolicyName !== null || e.timeOffPolicyID !== null) return false;
                    // field absent (undefined) is NOT "unassigned" per rollout rule
                    if (e.timeOffPolicyName === undefined) return false;
                } else {
                    if (String(e.timeOffPolicyID) !== timeOffFilter) return false;
                }
            }
            if (q) {
                if (isDigits) {
                    const idStr = e.altEmployeeID ? String(e.altEmployeeID) : String(e.employeeID);
                    const phoneDigits = e.phoneNumber ? e.phoneNumber.replace(/\D/g, "") : "";
                    if (!idStr.includes(q) && !phoneDigits.includes(q)) return false;
                } else if (hasLetters) {
                    if (!`${e.firstName} ${e.lastName}`.toLowerCase().includes(q.toLowerCase())) return false;
                } else {
                    return false;
                }
            }
            return true;
        });
        const rows =
            showPhoto && photoFilter !== "all"
                ? scopeRows.filter((e) => enrollmentState(e) === photoFilter)
                : scopeRows.slice();

        const attr = (e: EmployeeInfo): string => {
            switch (sortKey) {
                case "Name":
                    return `${e.lastName} ${e.firstName}`.toLowerCase();
                case "ID":
                    return (e.altEmployeeID ?? String(e.employeeID)).padStart(12, "0");
                case "Phone":
                    return e.phoneNumber ?? "";
                case "JobTitle":
                    return (e.jobTitle ?? "").toLowerCase();
                case "Role":
                    return getRoleDisplay(e.roleID).toLowerCase();
                case "LastLogin":
                    return applyOffsetToUTCDateTime(e.lastLoginDate, e.lastLoginDateOffset) ?? "";
                case "Supervisor":
                    return getSupervisorName(e.supervisorID).toLowerCase();
                case "PWClassification":
                    return (e.pwClassification?.name ?? "").toLowerCase();
                case "DriveTime":
                    return e.canAddDriveTime ? "1" : "0";
                case "CompanyPhone":
                    return e.hasCompanyPhone ? `1${e.companyPhoneStartDate ?? ""}` : "0";
                case "WorkOrders":
                    return e.canCreateWorkOrders ? "1" : "0";
                case "WORate":
                    return (e.workOrderRate ?? e.hourlyRate ?? 0).toFixed(2).padStart(12, "0");
                case "Photo":
                    // ascending: Failed → Needed → Verifying → On file
                    return String(PHOTO_SORT_RANK[enrollmentState(e)]);
                case "TimeOffPolicy":
                    // undefined (field absent) and null (unassigned) both sort LAST
                    return e.timeOffPolicyName != null ? e.timeOffPolicyName.toLowerCase() : "￿";
                default:
                    return "";
            }
        };

        const nameKey = (e: EmployeeInfo) => `${e.lastName} ${e.firstName}`.toLowerCase();
        rows.sort((a, b) => {
            if (dupPhonesOnly) {
                // grouped by number, active first; the column sort applies within a group
                const ga = dupGroupOf(a);
                const gb = dupGroupOf(b);
                const g = ga && gb ? duplicateSortKey(ga, a.active).localeCompare(duplicateSortKey(gb, b.active)) : 0;
                if (g !== 0) return g;
            }
            const cmp = attr(a).localeCompare(attr(b));
            if (cmp !== 0) return sortDirection === "asc" ? cmp : -cmp;
            return nameKey(a).localeCompare(nameKey(b)); // stable name tiebreak for boolean-ish keys
        });
        return { rows, scopeRows };
    }, [
        data,
        searchTerm,
        selectedLocation,
        selectedSuper,
        isActive,
        dupPhonesOnly,
        dupGroupOf,
        driveFilter,
        phoneFilter,
        woFilter,
        woRateAuditOnly,
        timeOffFilter,
        photoFilter,
        showDrive,
        showPhone,
        showWorkOrders,
        showTimeOff,
        showPhoto,
        sortKey,
        sortDirection,
        employeeByAltID,
        getRoleDisplay,
        getSupervisorName,
    ]);
    const processed = filtered.rows;
    const scopeRows = filtered.scopeRows;

    // ─── Pagination ───────────────────────────────────────────────────────────
    const total = processed.length;
    const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
    const page = Math.min(currentPage, totalPages);
    const start = (page - 1) * itemsPerPage;
    const pageRows = processed.slice(start, start + itemsPerPage);

    const pageWindow = (p: number, pages: number, max: number): (number | "…")[] => {
        if (pages <= max) return Array.from({ length: pages }, (_, i) => i + 1);
        const out: (number | "…")[] = [1];
        const lo = Math.max(2, p - 2);
        const hi = Math.min(pages - 1, p + 2);
        if (lo > 2) out.push("…");
        for (let i = lo; i <= hi; i++) out.push(i);
        if (hi < pages - 1) out.push("…");
        out.push(pages);
        return out;
    };

    const goPage = (n: number) => {
        setCurrentPage(n);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ─── Sorting ──────────────────────────────────────────────────────────────
    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDirection("asc");
        }
    };

    // ─── Filter chip clearing ─────────────────────────────────────────────────
    const clearFilters = () => {
        setSelectedLocation("ALL");
        setSelectedSuper("ALL");
        setDriveFilter("all");
        setWoFilter("all");
        setWoRateAuditOnly(false);
        setTimeOffFilter("all");
        setPhotoFilter("all");
        setSearchTerm("");
        setDupPhonesOnly(false);
    };

    // ─── Enrollment photo flow (TAS-220 / TAS-568) ────────────────────────────
    const canEditPhoto = userRole.admin || !!userRole.editEmployeeEnrollmentPhotos;
    const openPhotoModal = useCallback(
        (emp: Pick<EmployeeInfo, "employeeID" | "altEmployeeID" | "firstName" | "lastName" | "enrollment">) =>
            setEnrollmentEmployee({
                employeeID: emp.employeeID,
                altEmployeeID: emp.altEmployeeID,
                firstName: emp.firstName,
                lastName: emp.lastName,
                enrollment: emp.enrollment ?? null,
            }),
        []
    );

    /** The shared modal owns upload / retry / delete. The row is patched in place (no roster
     *  refetch), so the Photo button, avatar cue, callout and poll all re-derive at once. */
    const handleEnrollmentChanged = useCallback(
        (who: EnrollmentModalEmployee, next: EmployeeEnrollment, action: EnrollmentChangeAction) => {
            const photoPath = `/admin/employees/${who.employeeID}/enrollmentPhoto/image`;
            setData((prev) =>
                prev
                    ? prev.map((e) =>
                          e.employeeID !== who.employeeID
                              ? e
                              : {
                                    ...e,
                                    enrollment: next,
                                    // enrollmentPhotoUrl stays a presence flag for older readers
                                    enrollmentPhotoUrl: next.state === "none" ? null : e.enrollmentPhotoUrl || photoPath,
                                    enrolledAt:
                                        next.state === "none"
                                            ? null
                                            : next.enrolledAt ??
                                              (action === "retry" ? e.enrolledAt : new Date().toISOString()),
                                    isEnrolled: next.state === "enrolled",
                                }
                      )
                    : prev
            );
            setEnrollmentEmployee((cur) => (cur && cur.employeeID === who.employeeID ? { ...cur, enrollment: next } : cur));
            const name = <b>{`${who.firstName} ${who.lastName}`}</b>;
            if (action === "delete") {
                showToast({ kind: "ok", message: <>Enrollment photo deleted for {name}</> });
                return;
            }
            const lead = action === "retry" ? "Retry queued for" : action === "replace" ? "Photo replaced for" : "Photo uploaded for";
            showToast({
                kind: "ok",
                spin: next.state === "verifying",
                ms: 6000,
                message: (
                    <>
                        {lead} {name}. Verifying with Azure, usually within 15 minutes.
                    </>
                ),
            });
        },
        [showToast]
    );

    // Photo-status freshness (Q9): the manual Refresh and the 60s poll both re-read the roster
    // silently, then name the rows whose verification finished.
    const dataRef = useRef<EmployeeInfo[] | null>(data);
    dataRef.current = data;
    const refreshPhotoStatus = useCallback(
        async (manual: boolean) => {
            const before = new Map<number, EnrollmentState>();
            (dataRef.current ?? []).forEach((e) => before.set(e.employeeID, enrollmentState(e)));
            if (manual) setRefreshingPhotos(true);
            const fresh = await fetchData({ silent: true });
            if (manual) setRefreshingPhotos(false);
            if (!fresh) {
                if (manual) showToast({ kind: "err", message: "Couldn’t refresh photo status. Try again in a moment." });
                return;
            }
            const done = fresh.filter((e) => before.get(e.employeeID) === "verifying" && enrollmentState(e) !== "verifying");
            const verified = done.filter((e) => enrollmentState(e) === "enrolled");
            const failed = done.filter((e) => enrollmentState(e) === "failed");
            const nm = (e: EmployeeInfo) => <b>{`${e.firstName} ${e.lastName}`}</b>;
            if (failed.length) {
                showToast({
                    kind: "err",
                    message:
                        failed.length === 1 ? (
                            <>
                                Azure rejected the photo for {nm(failed[0])}: {enrollmentReason(failed[0].enrollment?.reason).label}
                            </>
                        ) : (
                            <>
                                Azure rejected <b>{failed.length}</b> photos. Filter Photo to <b>Failed</b> to fix them.
                            </>
                        ),
                });
            } else if (verified.length) {
                showToast({
                    kind: "ok",
                    message:
                        verified.length === 1 ? (
                            <>{nm(verified[0])} is now verified for face-match clock-in</>
                        ) : (
                            <>
                                <b>{verified.length}</b> photos are now verified for face-match clock-in
                            </>
                        ),
                });
            } else if (manual) {
                showToast({ kind: "ok", message: "Photo status is up to date" });
            }
        },
        [fetchData, showToast]
    );
    const refreshPhotoStatusRef = useRef(refreshPhotoStatus);
    refreshPhotoStatusRef.current = refreshPhotoStatus;

    // Poll GET /admin/employees every 60s ONLY while a row in scope is verifying; the interval
    // is torn down as soon as none are (or on unmount). Hidden tabs skip the tick.
    const verifyingInScope = useMemo(
        () => (showPhoto ? scopeRows.filter((e) => enrollmentState(e) === "verifying").length : 0),
        [scopeRows, showPhoto]
    );
    const photoPollOn = verifyingInScope > 0;
    useEffect(() => {
        if (!photoPollOn) return;
        const id = window.setInterval(() => {
            if (!document.hidden) refreshPhotoStatusRef.current(false);
        }, PHOTO_POLL_MS);
        return () => window.clearInterval(id);
    }, [photoPollOn]);

    // Deep link `?page=employeeView&photo={altEmployeeID}` (failure email / Fix enrollment):
    // consumed once, after the roster loads, and opens that employee's photo modal.
    useEffect(() => {
        if (photoDeepLinkConsumed.current || !showPhoto || !data) return;
        const stashed = peekStashedDeepLink();
        const link = pendingDeepLink?.photo ? pendingDeepLink : stashed?.photo ? stashed : null;
        if (!link?.photo) return;
        photoDeepLinkConsumed.current = true;
        if (stashed?.photo) consumeStashedDeepLink();
        const emp = data.find((e) => e.altEmployeeID === link.photo);
        if (!emp) {
            showToast({ kind: "err", message: <>Employee <b>{link.photo}</b> isn’t on the roster.</> });
            return;
        }
        if (emp.active !== isActive) setIsActive(emp.active);
        openPhotoModal(emp);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot; isActive read at consumption time
    }, [data, pendingDeepLink, showPhoto, openPhotoModal, showToast]);

    // ─── Drive-time flow ──────────────────────────────────────────────────────
    const commitDrive = useCallback(
        async (emp: EmployeeInfo, next: boolean) => {
            setSavingDrive((prev) => new Set(prev).add(emp.employeeID));
            setData((prev) =>
                prev ? prev.map((e) => (e.employeeID === emp.employeeID ? { ...e, canAddDriveTime: next } : e)) : prev
            );
            const name = `${emp.firstName} ${emp.lastName}`;
            try {
                await EmployeeViewManager.updateEmployeeCanAddDriveTime(emp.employeeID, next);
                setSavingDrive((prev) => {
                    const s = new Set(prev);
                    s.delete(emp.employeeID);
                    return s;
                });
                showToast({
                    kind: "ok",
                    message: (
                        <>
                            Drive time {next ? "enabled" : "disabled"} for <b>{name}</b>
                        </>
                    ),
                    actionLabel: "Undo",
                    onAction: () => commitDrive(emp, !next),
                });
            } catch (error) {
                // revert
                setData((prev) =>
                    prev
                        ? prev.map((e) => (e.employeeID === emp.employeeID ? { ...e, canAddDriveTime: !next } : e))
                        : prev
                );
                setSavingDrive((prev) => {
                    const s = new Set(prev);
                    s.delete(emp.employeeID);
                    return s;
                });
                showToast({
                    kind: "err",
                    message: (
                        <>
                            Couldn’t save drive-time setting for <b>{name}</b>. Reverted.
                        </>
                    ),
                    actionLabel: "Retry",
                    onAction: () => commitDrive(emp, next),
                });
            }
        },
        [showToast]
    );

    const confirmDrive = () => {
        if (!driveConfirm) return;
        const { emp, next } = driveConfirm;
        setDriveConfirm(null);
        commitDrive(emp, next);
    };

    // ─── Company-phone flow ───────────────────────────────────────────────────
    // Optimistic flip of hasCompanyPhone + companyPhoneStartDate, then the API call.
    // Undo re-sends the previous state (including the previous date). Error → revert + Retry.
    const commitPhone = useCallback(
        async (emp: EmployeeInfo, next: boolean, startDate: string | null) => {
            const prevHas = emp.hasCompanyPhone;
            const prevDate = emp.companyPhoneStartDate ?? null;
            const apply = (has: boolean, date: string | null) =>
                setData((prev) =>
                    prev
                        ? prev.map((e) =>
                              e.employeeID === emp.employeeID
                                  ? { ...e, hasCompanyPhone: has, companyPhoneStartDate: date }
                                  : e
                          )
                        : prev
                );
            const clearSaving = () =>
                setSavingPhone((prev) => {
                    const s = new Set(prev);
                    s.delete(emp.employeeID);
                    return s;
                });
            setSavingPhone((prev) => new Set(prev).add(emp.employeeID));
            apply(next, next ? startDate : null);
            const name = `${emp.firstName} ${emp.lastName}`;
            try {
                await EmployeeViewManager.updateEmployeeCompanyPhone(
                    emp.employeeID,
                    next,
                    next ? startDate ?? undefined : undefined
                );
                clearSaving();
                showToast({
                    kind: "ok",
                    message: (
                        <>
                            Company phone {next ? `marked as issued ${fmtISODate(startDate)}` : "removed"} for <b>{name}</b>
                        </>
                    ),
                    actionLabel: "Undo",
                    onAction: () =>
                        commitPhone({ ...emp, hasCompanyPhone: next, companyPhoneStartDate: next ? startDate : null }, prevHas, prevDate),
                });
            } catch (error) {
                apply(prevHas, prevDate);
                clearSaving();
                showToast({
                    kind: "err",
                    message: (
                        <>
                            Couldn’t save company phone setting for <b>{name}</b>. Reverted.
                        </>
                    ),
                    actionLabel: "Retry",
                    onAction: () => commitPhone(emp, next, startDate),
                });
            }
        },
        [showToast]
    );

    const confirmPhone = () => {
        if (!phoneConfirm) return;
        const { emp, next } = phoneConfirm;
        if (next && !/^\d{4}-\d{2}-\d{2}$/.test(phoneDate)) return;
        setPhoneConfirm(null);
        commitPhone(emp, next, next ? phoneDate : null);
    };

    // ─── Activate / inactivate flow ───────────────────────────────────────────
    // Optimistic flip of e.active (the row leaves the currently filtered roster),
    // then the API call. Success → toast with Undo (same endpoint, previous value,
    // no confirm). Error → revert the flip + toast with Retry.
    const commitStatus = useCallback(
        async (emp: EmployeeInfo, next: boolean) => {
            setData((prev) =>
                prev ? prev.map((e) => (e.employeeID === emp.employeeID ? { ...e, active: next } : e)) : prev
            );
            const name = `${emp.firstName} ${emp.lastName}`;
            try {
                await EmployeeViewManager.updateEmployeeActive(emp.employeeID, next);
                showToast({
                    kind: "ok",
                    message: (
                        <>
                            <b>{name}</b> {next ? "reactivated" : "deactivated"}
                        </>
                    ),
                    actionLabel: "Undo",
                    onAction: () => commitStatus(emp, !next),
                });
            } catch (error) {
                // revert
                setData((prev) =>
                    prev ? prev.map((e) => (e.employeeID === emp.employeeID ? { ...e, active: !next } : e)) : prev
                );
                showToast({
                    kind: "err",
                    message: (
                        <>
                            Couldn’t update status for <b>{name}</b>. Reverted.
                        </>
                    ),
                    actionLabel: "Retry",
                    onAction: () => commitStatus(emp, next),
                });
            }
        },
        [showToast]
    );

    const confirmStatus = () => {
        if (!statusConfirm) return;
        const { emp, next } = statusConfirm;
        setStatusConfirm(null);
        commitStatus(emp, next);
    };

    // Esc dismisses the status confirm modal (Cancel / backdrop also dismiss).
    useEffect(() => {
        if (!statusConfirm) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setStatusConfirm(null);
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [statusConfirm]);

    // ─── Work-order-creation flow ───────────────────────────────────────────────
    const commitWO = useCallback(
        async (emp: EmployeeInfo, next: boolean) => {
            setSavingWO((prev) => new Set(prev).add(emp.employeeID));
            setData((prev) =>
                prev ? prev.map((e) => (e.employeeID === emp.employeeID ? { ...e, canCreateWorkOrders: next } : e)) : prev
            );
            const name = `${emp.firstName} ${emp.lastName}`;
            try {
                await EmployeeViewManager.updateEmployeeCanCreateWorkOrders(emp.employeeID, next);
                setSavingWO((prev) => {
                    const s = new Set(prev);
                    s.delete(emp.employeeID);
                    return s;
                });
                showToast({
                    kind: "ok",
                    message: (
                        <>
                            Work order creation {next ? "enabled" : "disabled"} for <b>{name}</b>
                        </>
                    ),
                    actionLabel: "Undo",
                    onAction: () => commitWO(emp, !next),
                });
            } catch (error) {
                // revert
                setData((prev) =>
                    prev
                        ? prev.map((e) => (e.employeeID === emp.employeeID ? { ...e, canCreateWorkOrders: !next } : e))
                        : prev
                );
                setSavingWO((prev) => {
                    const s = new Set(prev);
                    s.delete(emp.employeeID);
                    return s;
                });
                showToast({
                    kind: "err",
                    message: (
                        <>
                            Couldn’t save work-order setting for <b>{name}</b>. Reverted.
                        </>
                    ),
                    actionLabel: "Retry",
                    onAction: () => commitWO(emp, next),
                });
            }
        },
        [showToast]
    );

    const confirmWO = () => {
        if (!woConfirm) return;
        const { emp, next } = woConfirm;
        setWoConfirm(null);
        commitWO(emp, next);
    };

    // ─── Per-row re-sync ──────────────────────────────────────────────────────
    const handleEmployeeSync = async (
        employeeAltID: string,
        employeeAltLocationID: string,
        name?: string,
        employeeID?: number
    ) => {
        if (!employeeAltID) return;
        if (employeeID != null) setSyncingRows((prev) => new Set(prev).add(employeeID));
        else setLoading(true);
        try {
            const response = await EmployeeViewManager.syncEmployeeData(employeeAltID, employeeAltLocationID);
            let popupMessage =
                response.message || (response.success ? "Sync completed successfully!" : "Sync failed! Please try again.");
            if (response.updates && response.updates.length > 0) {
                popupMessage += "\n\nUpdates:\n" + response.updates.join("\n");
            }
            createPopup(`Data Sync for ${name ?? ""} (${employeeAltID})`, popupMessage);
        } catch (error) {
            console.error("Error during sync:", error);
            createPopup(`Data Sync for ${name ?? ""} (${employeeAltID})`, "Sync failed! Please try again.");
        } finally {
            await fetchData();
            if (employeeID != null)
                setSyncingRows((prev) => {
                    const s = new Set(prev);
                    s.delete(employeeID);
                    return s;
                });
            else setLoading(false);
        }
    };

    // ─── Time-off policy modal ─────────────────────────────────────────────────
    // Balances feed (endpoint 1) — shared by the Balances table and the Activity
    // category picker. Fetched fresh on every modal open (assigned employees only).
    const loadToBalances = useCallback(async (employeeID: number) => {
        setToBalancesLoading(true);
        setToBalancesError(false);
        try {
            const rows = await getEmployeeTimeOffBalance(employeeID);
            setToBalances(rows);
            // Activity tab defaults to the first category that has a ledger — Unpaid Time
            // (TAS-586) has no balance, so it never appears in the Activity picker.
            const firstLedger = rows.find((r) => !isUnpaid(r));
            setToSelectedCatID((prev) => prev ?? (firstLedger ? firstLedger.timeOffCategoryID : null));
        } catch (err) {
            console.error("Failed to load time-off balances", err);
            setToBalancesError(true);
        } finally {
            setToBalancesLoading(false);
        }
    }, []);

    // Ledger feed (endpoint 2) — page 0 on category select, next pages via Load
    // more (appended). Pages are cached per category while the modal is open.
    const loadToLedgerPage = useCallback(async (employeeID: number, categoryID: number, pageToFetch: number) => {
        setToLedgers((prev) => ({
            ...prev,
            [categoryID]:
                pageToFetch === 0
                    ? emptyLedgerState()
                    : { ...(prev[categoryID] ?? emptyLedgerState()), loadingMore: true, error: false },
        }));
        try {
            const res = await getEmployeeTimeOffLedger(employeeID, categoryID, pageToFetch, LEDGER_PAGE_SIZE);
            setToLedgers((prev) => {
                const cur = prev[categoryID];
                const prevItems = pageToFetch === 0 || !cur ? [] : cur.items;
                return {
                    ...prev,
                    [categoryID]: {
                        loading: false,
                        loadingMore: false,
                        error: false,
                        currentBalanceHours: res.currentBalanceHours,
                        pending: res.pending,
                        items: [...prevItems, ...res.items],
                        total: res.total,
                        nextPage: res.page + 1,
                    },
                };
            });
        } catch (err) {
            console.error("Failed to load time-off ledger", err);
            setToLedgers((prev) => ({
                ...prev,
                [categoryID]: {
                    ...(prev[categoryID] ?? emptyLedgerState()),
                    loading: false,
                    loadingMore: false,
                    error: true,
                },
            }));
        }
    }, []);

    const openTimeOffModal = useCallback(async (emp: EmployeeInfo) => {
        setToEmployee(emp);
        setToLoading(true);
        setToDataFetched(false);
        setToCurrent(null);
        setToHistory([]);
        setToSelectedPolicyID("");
        setToStartDate(todayISO());
        setToNotes("");
        setToDateError(false);
        setToSaving(false);
        // Read-only tabs: refetch fresh every open (no cross-open caching).
        setToTab("balances");
        setToBalances(null);
        setToBalancesLoading(false);
        setToBalancesError(false);
        setToSelectedCatID(null);
        setToLedgers({});
        // Resolve admin names for the "Assigned by" line — cosmetic, fire-and-forget,
        // fetched once per page load (falls back to "admin #N" until/unless it loads).
        if (!adminNamesFetchedRef.current) {
            adminNamesFetchedRef.current = true;
            EmployeeViewManager.getAdminEmployees()
                .then((admins) =>
                    setAdminNames(new Map(admins.map((a) => [a.adminEmployeeID, `${a.name.first} ${a.name.last}`.trim()])))
                )
                .catch(() => { /* keep the #ID fallback */ });
        }
        try {
            const [policyData, activePolicies] = await Promise.all([
                getEmployeeTimeOffPolicy(emp.employeeID),
                getTimeOffPolicies(true),
            ]);
            setToCurrent(policyData.current);
            setToHistory(policyData.history);
            setToActivePolicies(activePolicies);
            // Default select: current policy if set, else first active
            if (policyData.current) {
                setToSelectedPolicyID(policyData.current.timeOffPolicyID);
            } else if (activePolicies.length > 0) {
                setToSelectedPolicyID(activePolicies[0].timeOffPolicyID);
            }
            // Open on Balances when a policy is assigned, on Assignment when not
            // (unassigned employees have no balances/ledger to fetch).
            if (policyData.current) {
                setToTab("balances");
                loadToBalances(emp.employeeID);
            } else {
                setToTab("assignment");
            }
            setToDataFetched(true);
        } catch (err) {
            console.error("Failed to load time-off policy data", err);
            showToast({ kind: "err", message: "Failed to load time off policy data. Please try again." });
            setToEmployee(null);
        } finally {
            setToLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast/loadToBalances are stable
    }, []);

    const closeTimeOffModal = useCallback(() => {
        setToEmployee(null);
        setToDataFetched(false);
    }, []);

    // Activity tab: fetch page 0 for the selected category on first view/select.
    // Already-fetched categories (cache entry present, including in-flight or
    // errored ones — errors retry manually) are left alone.
    useEffect(() => {
        if (!toEmployee || toTab !== "activity" || toSelectedCatID == null) return;
        if (toLedgers[toSelectedCatID]) return;
        loadToLedgerPage(toEmployee.employeeID, toSelectedCatID, 0);
    }, [toEmployee, toTab, toSelectedCatID, toLedgers, loadToLedgerPage]);

    // Esc closes the time-off modal (matching the app's other v2 modals).
    useEffect(() => {
        if (!toEmployee) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeTimeOffModal();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [toEmployee, closeTimeOffModal]);

    const handleSaveTimeOffPolicy = useCallback(async () => {
        if (!toEmployee || !toSelectedPolicyID) return;
        if (!toStartDate) {
            setToDateError(true);
            return;
        }
        setToSaving(true);
        const pol = toActivePolicies.find((p) => p.timeOffPolicyID === toSelectedPolicyID);
        const wasUnassigned = !toCurrent;
        const name = `${toEmployee.firstName} ${toEmployee.lastName}`;
        try {
            await assignEmployeeTimeOffPolicy(toEmployee.employeeID, {
                timeOffPolicyID: toSelectedPolicyID as number,
                startDate: toStartDate,
                notes: toNotes.trim() || undefined,
            });
            // Update the roster row in place
            setData((prev) =>
                prev
                    ? prev.map((e) =>
                          e.employeeID === toEmployee.employeeID
                              ? {
                                    ...e,
                                    timeOffPolicyID: toSelectedPolicyID as number,
                                    timeOffPolicyName: pol?.name ?? null,
                                }
                              : e
                      )
                    : prev
            );
            closeTimeOffModal();
            showToast({
                kind: "ok",
                message: (
                    <>
                        {wasUnassigned ? "Assigned" : "Changed to"} <b>{pol?.name ?? "policy"}</b> for <b>{name}</b>,
                        effective {fmtISO(toStartDate)}
                    </>
                ),
            });
        } catch (err) {
            showToast({ kind: "err", message: `Failed to save time off policy for ${name}. Please try again.` });
        } finally {
            setToSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast stable, closeTimeOffModal stable
    }, [toEmployee, toSelectedPolicyID, toStartDate, toNotes, toActivePolicies, toCurrent, closeTimeOffModal]);

    // ─── Time-off modal: read-only pane renderers ─────────────────────────────
    /** Unassigned empty state shown on both read-only tabs (handoff `toEmptyTO`). */
    const renderToNoPolicy = (sub: string) => (
        <div className="emp-to-empty">
            <AlertTriangle size={19} />
            <div>
                <div className="emp-to-empty-t">No time off policy assigned</div>
                <div className="emp-to-empty-s">{sub}</div>
            </div>
        </div>
    );

    const renderToFetchError = (message: string, onRetry: () => void) => (
        <div className="emp-to-fetch-err">
            <AlertTriangle size={15} />
            {message}
            <button type="button" className="emp-btn sm" onClick={onRetry}>
                Retry
            </button>
        </div>
    );

    const renderToBalancesPane = () => {
        if (!toCurrent) {
            return renderToNoPolicy(
                "Assign a policy on the Assignment tab to start tracking accruals and activity."
            );
        }
        if (toBalancesLoading) {
            return (
                <div className="emp-to-loading">
                    <span className="emp-spin" /> Loading balances…
                </div>
            );
        }
        if (toBalancesError || !toBalances) {
            return renderToFetchError("Couldn’t load balances.", () =>
                loadToBalances(toEmployee!.employeeID)
            );
        }
        if (toBalances.length === 0) {
            return <div className="emp-led-none">No time off categories are enabled for this policy.</div>;
        }
        return <TimeOffBalancesTable rows={toBalances} policyName={toCurrent.policyName} />;
    };

    const renderToLedgerRow = (entry: TimeOffLedgerEntry, pinnedPending: boolean, key: string) => {
        const pending = pinnedPending || entry.kind === "pending";
        const pos = entry.hours >= 0;
        return (
            <div key={key} className={`emp-led-row${pending ? " pending" : ""}`}>
                <div className="emp-led-date emp-mono">{fmtLedgerDate(entry.date)}</div>
                <div className="emp-led-main">
                    <span className={`emp-led-type ${entry.kind}`}>{entry.label}</span>
                    {pending && <span className="emp-led-tag">Pending</span>}
                </div>
                <div className={`emp-led-hrs ${pos ? "pos" : "neg"}`}>
                    {pos ? "+" : "−"}
                    {fmtHrs(Math.abs(entry.hours))}
                </div>
                <div className="emp-led-bal emp-mono">
                    {entry.balanceAfter == null ? DASH : fmtHrs(entry.balanceAfter)}
                </div>
            </div>
        );
    };

    const renderToLedger = (cat: EmployeeTimeOffBalanceRow) => {
        const st = toLedgers[cat.timeOffCategoryID];
        if (!st || st.loading) {
            return (
                <div className="emp-to-loading">
                    <span className="emp-spin" /> Loading activity…
                </div>
            );
        }
        if (st.error && st.items.length === 0 && st.pending.length === 0) {
            return renderToFetchError("Couldn’t load activity.", () =>
                loadToLedgerPage(toEmployee!.employeeID, cat.timeOffCategoryID, 0)
            );
        }
        const hasRows = st.items.length > 0 || st.pending.length > 0;
        return (
            <>
                <div className="emp-led-cur">
                    <span className="emp-led-cur-lab">{cat.categoryName} available</span>
                    <span className="emp-led-cur-val">
                        {fmtHrs(st.currentBalanceHours)}
                        <span className="emp-led-cur-days"> · {fmtDays(st.currentBalanceHours)}</span>
                    </span>
                </div>
                <div className="emp-led-head">
                    <span>Date</span>
                    <span>Activity</span>
                    <span>Change</span>
                    <span>Balance</span>
                </div>
                <div className="emp-ledger">
                    {/* Pending requests pinned on top — no running-balance effect */}
                    {st.pending.map((x, i) => renderToLedgerRow(x, true, `p${i}`))}
                    {st.items.map((x, i) => renderToLedgerRow(x, false, `i${i}`))}
                    {!hasRows && <div className="emp-led-none">No activity recorded yet.</div>}
                </div>
                {st.items.length < st.total && (
                    <div className="emp-led-more">
                        {st.error && (
                            <span className="emp-led-more-err">
                                <AlertTriangle size={14} /> Couldn’t load more activity.
                            </span>
                        )}
                        <button
                            type="button"
                            className="emp-btn sm"
                            disabled={st.loadingMore}
                            onClick={() =>
                                loadToLedgerPage(toEmployee!.employeeID, cat.timeOffCategoryID, st.nextPage)
                            }
                        >
                            {st.loadingMore ? (
                                <>
                                    <span className="emp-spin" /> Loading…
                                </>
                            ) : st.error ? (
                                "Retry"
                            ) : (
                                "Load more"
                            )}
                        </button>
                    </div>
                )}
            </>
        );
    };

    const renderToActivityPane = () => {
        if (!toCurrent) {
            return renderToNoPolicy("Once a policy is assigned, accrual and usage activity will appear here.");
        }
        if (toBalancesLoading) {
            return (
                <div className="emp-to-loading">
                    <span className="emp-spin" /> Loading activity…
                </div>
            );
        }
        if (toBalancesError || !toBalances) {
            return renderToFetchError("Couldn’t load activity.", () =>
                loadToBalances(toEmployee!.employeeID)
            );
        }
        if (toBalances.length === 0) {
            return <div className="emp-led-none">No time off categories are enabled for this policy.</div>;
        }
        // Unpaid Time has no balance and an always-empty ledger, so it has no Activity entry.
        const ledgerCats = toBalances.filter((c) => !isUnpaid(c));
        if (ledgerCats.length === 0) {
            return <div className="emp-led-none">Unpaid time has no balance, so there is no activity to show.</div>;
        }
        const selectedCat =
            ledgerCats.find((c) => c.timeOffCategoryID === toSelectedCatID) ?? ledgerCats[0];
        return (
            <>
                {/* Category picker — scopes the ledger to one category at a time */}
                <div className="emp-segmented emp-cat-seg">
                    {ledgerCats.map((c) => (
                        <button
                            key={c.timeOffCategoryID}
                            type="button"
                            className={c.timeOffCategoryID === selectedCat.timeOffCategoryID ? "on" : ""}
                            onClick={() => setToSelectedCatID(c.timeOffCategoryID)}
                        >
                            {c.categoryName}
                        </button>
                    ))}
                </div>
                {renderToLedger(selectedCat)}
            </>
        );
    };

    // ─── Role save ────────────────────────────────────────────────────────────
    const handleRoleSave = async (employeeID: number, roleID: number) => {
        setLoading(true);
        try {
            await EmployeeViewManager.updateEmployeeRole(employeeID, roleID);
            setRoleEditEmployee(null);
            await fetchData();
            createPopup("Role Update", "Employee role updated successfully");
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 403) createPopup("Role Update", "Not authorized to edit employee roles");
            else if (status === 400) createPopup("Role Update", "Invalid employeeID or roleID");
            else if (status === 404) createPopup("Role Update", "Employee not found");
            else createPopup("Role Update", "Error updating employee role");
        } finally {
            setLoading(false);
        }
    };

    // ─── Derived UI bits ──────────────────────────────────────────────────────
    const visibleColCount =
        5 +
        (showPW ? 1 : 0) +
        (showTimeOff ? 1 : 0) +
        (showDrive ? 1 : 0) +
        (showPhone ? 1 : 0) +
        (showWorkOrders ? 1 : 0) +
        (showWorkOrders ? 1 : 0) + // Work orders + WO Rate, both gated on showWorkOrdersPage
        (showPhoto ? 1 : 0);

    const sortOptions: SortKey[] = [
        "Name",
        "ID",
        "Phone",
        "JobTitle",
        "Role",
        "LastLogin",
        "Supervisor",
        ...(showPW ? (["PWClassification"] as SortKey[]) : []),
        ...(showTimeOff ? (["TimeOffPolicy"] as SortKey[]) : []),
        ...(showDrive ? (["DriveTime"] as SortKey[]) : []),
        ...(showPhone ? (["CompanyPhone"] as SortKey[]) : []),
        ...(showWorkOrders ? (["WorkOrders"] as SortKey[]) : []),
        ...(showWorkOrders ? (["WORate"] as SortKey[]) : []),
        ...(showPhoto ? (["Photo"] as SortKey[]) : []),
    ];

    // Distinct time-off policy names present in the loaded roster (for filter options)
    const timeOffPolicyOptions = useMemo((): { id: string; name: string }[] => {
        if (!data) return [];
        const seen = new Map<string, string>();
        for (const e of data) {
            if (e.timeOffPolicyID != null && e.timeOffPolicyName != null) {
                seen.set(String(e.timeOffPolicyID), e.timeOffPolicyName);
            }
        }
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data]);

    const supervisorChipLabel = (v: string): string => {
        if (v === "0") return "Supervisors (top level)";
        const s = employeeByAltID.get(v);
        return s ? `${s.altEmployeeID} — ${s.firstName} ${s.lastName}` : v;
    };

    const locationChipLabel = (v: string): string => {
        const l = locations?.find((loc) => loc.locationID === parseInt(v, 10));
        return l ? `${l.altLocationID ? `${l.altLocationID} — ` : ""}${l.name}` : v;
    };

    const timeOffChipLabel = (v: string): string => {
        if (v === "unassigned") return "Unassigned";
        const opt = timeOffPolicyOptions.find((o) => o.id === v);
        return opt ? opt.name : v;
    };

    // ─── Enrollment photo coverage (derived, never stored) ───────────────────
    // Scope = every filter except the Photo filter, so the counts never collapse once you
    // narrow to one state. Coverage counts Verifying as NOT covered ("% verified").
    const photoCounts = useMemo(() => {
        const c: Record<EnrollmentState, number> = { none: 0, failed: 0, verifying: 0, enrolled: 0 };
        scopeRows.forEach((e) => {
            c[enrollmentState(e)]++;
        });
        return c;
    }, [scopeRows]);
    const photoGap = photoCounts.none + photoCounts.failed + photoCounts.verifying;
    const coveragePct = Math.round((photoCounts.enrolled / Math.max(scopeRows.length, 1)) * 100);
    // The list handed to a site: no photo + failed enrollment. Verifying is excluded — it may still pass.
    const missingRows: MissingPhotoRow[] = useMemo(
        () =>
            orderNeedingPhotos(
                scopeRows
                    .filter((e) => {
                        const s = enrollmentState(e);
                        return s === "none" || s === "failed";
                    })
                    .map((e) => {
                        const sup = getSupervisorName(e.supervisorID);
                        const state = enrollmentState(e) as "none" | "failed";
                        return {
                            employeeID: e.employeeID,
                            altEmployeeID: e.altEmployeeID ?? "",
                            firstName: e.firstName,
                            lastName: e.lastName,
                            name: `${e.firstName} ${e.lastName}`,
                            role: getRoleDisplay(e.roleID),
                            jobTitle: e.jobTitle ?? "",
                            supervisor: sup === DASH ? "" : sup,
                            locationLabel: locationChipLabel(String(e.locationID)),
                            phone: formatPhoneNumber(e.phoneNumber) ?? "",
                            state,
                            enrollment: e.enrollment,
                            status: enrollmentStatusText(state, e.enrollment),
                        };
                    })
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- locationChipLabel is a plain closure over `locations`
        [scopeRows, getSupervisorName, getRoleDisplay, locations]
    );
    const needingPhotosScopeLabel = [
        `${missingRows.length} ${missingRows.length === 1 ? "employee" : "employees"}`,
        selectedLocation !== "ALL" ? `Location ${locationChipLabel(selectedLocation)}` : null,
        selectedSuper !== "ALL" ? `Supervisor ${supervisorChipLabel(selectedSuper)}` : null,
        dupPhonesOnly ? "Duplicate phones" : isActive ? "Active" : "Inactive",
    ]
        .filter(Boolean)
        .join(" · ");

    // ─── Cell renderers ───────────────────────────────────────────────────────
    const renderRoleCell = (emp: EmployeeInfo) =>
        canEdit ? (
            <button type="button" className="emp-linkbtn" onClick={() => setRoleEditEmployee(emp)}>
                {getRoleDisplay(emp.roleID)}
            </button>
        ) : (
            <span>{getRoleDisplay(emp.roleID)}</span>
        );

    const renderPWCell = (emp: EmployeeInfo) => {
        const hasPW = !!emp.pwClassification?.name;
        if (canEdit) {
            return (
                <button
                    type="button"
                    className={`emp-linkbtn${hasPW ? "" : " muted"}`}
                    onClick={() => setPWClassificationModalEmployee(emp)}
                >
                    {hasPW ? formatPWClassification(emp) : DASH}
                </button>
            );
        }
        return <span>{hasPW ? formatPWClassification(emp) : DASH}</span>;
    };

    const renderTimeOffCell = (emp: EmployeeInfo) => {
        const hasField = emp.timeOffPolicyName !== undefined;
        const isNull = hasField && emp.timeOffPolicyName === null;
        const isAssigned = hasField && emp.timeOffPolicyName != null;

        if (isAssigned) {
            // Assigned: policy name — accent link (editable) or plain text (read-only)
            return canEdit ? (
                <button type="button" className="emp-linkbtn" onClick={() => openTimeOffModal(emp)}>
                    {emp.timeOffPolicyName}
                </button>
            ) : (
                <span>{emp.timeOffPolicyName}</span>
            );
        }
        if (isNull) {
            // Truly Unassigned (field present, value null) — amber warn pill
            const pill = (
                <span className="emp-to-unassigned">
                    <AlertTriangle size={13} />
                    Unassigned
                </span>
            );
            return canEdit ? (
                <button type="button" className="emp-linkbtn emp-to-unassigned-btn" onClick={() => openTimeOffModal(emp)}>
                    {pill}
                </button>
            ) : (
                pill
            );
        }
        // Field absent (undefined) — muted dash; still clickable when editable
        return canEdit ? (
            <button type="button" className="emp-linkbtn muted" onClick={() => openTimeOffModal(emp)}>
                {DASH}
            </button>
        ) : (
            <span>{DASH}</span>
        );
    };

    const renderDriveCell = (emp: EmployeeInfo) => {
        if (savingDrive.has(emp.employeeID)) {
            return (
                <div className="emp-dt-cell">
                    <span className="emp-dt-saving">
                        <span className="emp-spin" />
                        Saving…
                    </span>
                </div>
            );
        }
        if (!canEdit) {
            return (
                <div className="emp-dt-cell">
                    <span className={`emp-dt-pill ${emp.canAddDriveTime ? "on" : "off"}`}>
                        <span className="dot" />
                        {emp.canAddDriveTime ? "On" : "Off"}
                    </span>
                </div>
            );
        }
        return (
            <div className="emp-dt-cell">
                <label className="emp-switch">
                    <input
                        type="checkbox"
                        checked={emp.canAddDriveTime}
                        onChange={() => setDriveConfirm({ emp, next: !emp.canAddDriveTime })}
                    />
                    <span className="emp-track" />
                </label>
            </div>
        );
    };

    const renderPhoneCell = (emp: EmployeeInfo) => {
        if (savingPhone.has(emp.employeeID)) {
            return (
                <div className="emp-dt-cell">
                    <span className="emp-dt-saving">
                        <span className="emp-spin" />
                        Saving…
                    </span>
                </div>
            );
        }
        const pill = (
            <span className={`emp-dt-pill ${emp.hasCompanyPhone ? "on" : "off"}`}>
                <span className="dot" />
                {emp.hasCompanyPhone
                    ? emp.companyPhoneStartDate
                        ? `Yes since ${fmtISODate(emp.companyPhoneStartDate)}`
                        : "Yes"
                    : "No"}
            </span>
        );
        if (!canManagePhones) {
            return <div className="emp-dt-cell">{pill}</div>;
        }
        return (
            <div className="emp-dt-cell">
                <label className="emp-switch">
                    <input
                        type="checkbox"
                        checked={emp.hasCompanyPhone}
                        onChange={() => {
                            setPhoneDate(todayLocalISO());
                            setPhoneConfirm({ emp, next: !emp.hasCompanyPhone });
                        }}
                    />
                    <span className="emp-track" />
                </label>
                {pill}
            </div>
        );
    };

    const renderWOCell = (emp: EmployeeInfo) => {
        if (savingWO.has(emp.employeeID)) {
            return (
                <div className="emp-dt-cell">
                    <span className="emp-dt-saving">
                        <span className="emp-spin" />
                        Saving…
                    </span>
                </div>
            );
        }
        if (!canEdit) {
            return (
                <div className="emp-dt-cell">
                    <span className={`emp-dt-pill ${emp.canCreateWorkOrders ? "on" : "off"}`}>
                        <span className="dot" />
                        {emp.canCreateWorkOrders ? "On" : "Off"}
                    </span>
                </div>
            );
        }
        return (
            <div className="emp-dt-cell">
                <label className="emp-switch">
                    <input
                        type="checkbox"
                        checked={emp.canCreateWorkOrders}
                        onChange={() => setWoConfirm({ emp, next: !emp.canCreateWorkOrders })}
                    />
                    <span className="emp-track" />
                </label>
            </div>
        );
    };

    const renderWORateCell = (emp: EmployeeInfo) => {
        const hasOverride = emp.workOrderRate != null;
        const rate = hasOverride ? emp.workOrderRate! : emp.hourlyRate;
        const display = rate != null ? `$${rate.toFixed(2)}` : DASH;
        // TAS-292 (8/19): make overrides obvious at a glance — signed delta vs the
        // system rate, green when above, amber when below.
        const delta = hasOverride && emp.hourlyRate != null ? emp.workOrderRate! - emp.hourlyRate : null;
        const deltaText = delta == null || Math.abs(delta) < 0.005
            ? null
            : `${delta > 0 ? "+" : "−"}$${Math.abs(delta).toFixed(2)}`;
        // Clicking always opens the modal (view + history); the modal itself gates
        // the change form on manageWorkOrderRates and renders read-only otherwise.
        return (
            <button
                type="button"
                className={`emp-linkbtn${hasOverride ? "" : " muted"}`}
                onClick={() => setWoRateModalEmployee(emp)}
            >
                {display}
                {!hasOverride && rate != null && <span className="c-sub"> (system)</span>}
                {deltaText && (
                    <span
                        className="c-sub"
                        style={{ color: delta! > 0 ? "#176646" : "#96610f", fontWeight: 600, marginLeft: 4 }}
                        title={`System rate $${emp.hourlyRate!.toFixed(2)}`}
                    >
                        {deltaText}
                    </span>
                )}
            </button>
        );
    };

    // One control, four states: the camera button carries its own status badge — green check
    // (verified), slate spinning ring (verifying), red "!" (Azure rejected it), amber dot (needed) —
    // and opens the shared photo modal. It lives in its own sortable Photo column.
    const renderPhotoCell = (emp: EmployeeInfo) => {
        const s = enrollmentState(emp);
        const n = emp.enrollment;
        const date = formatPhotoDate(n?.enrolledAt ?? emp.enrolledAt);
        const attempts = n?.attempts ?? 1;
        const title =
            s === "none"
                ? "Photo needed — upload enrollment photo"
                : s === "enrolled"
                ? `Enrollment photo · verified${date ? ` · uploaded ${date}` : ""}`
                : s === "verifying"
                ? `Verifying with Azure · uploaded ${fmtWhen(n?.queuedAt ?? n?.enrolledAt ?? emp.enrolledAt)} · usually within 15 minutes${
                      attempts > 1 ? ` (attempt ${attempts} of 3)` : ""
                  }`
                : `Enrollment failed · ${enrollmentReason(n?.reason).label}`;
        const cls = { none: "need", enrolled: "ok", verifying: "verifying", failed: "failed" }[s];
        return (
            <button
                type="button"
                className={`emp-iconbtn emp-ph-btn ${cls}`}
                title={title}
                aria-label={title}
                onClick={() => openPhotoModal(emp)}
            >
                <Camera size={16} />
                <span className="badge">
                    {s === "enrolled" && <Check size={8} strokeWidth={4.5} />}
                    {s === "failed" && "!"}
                </span>
            </button>
        );
    };

    const renderActionsCell = (emp: EmployeeInfo) => (
        <div className="emp-act-cluster">
            <button
                type="button"
                className="emp-iconbtn"
                title="Re-sync employee"
                disabled={syncingRows.has(emp.employeeID)}
                onClick={() =>
                    handleEmployeeSync(
                        emp.altEmployeeID,
                        emp.altLocationID,
                        `${emp.firstName} ${emp.lastName}`,
                        emp.employeeID
                    )
                }
            >
                {syncingRows.has(emp.employeeID) ? <span className="emp-spin" /> : <RefreshCw size={16} />}
            </button>
            {canActivate &&
                (emp.active ? (
                    <button
                        type="button"
                        className="emp-iconbtn emp-status-btn deact"
                        title="Deactivate employee"
                        onClick={() => setStatusConfirm({ emp, next: false })}
                    >
                        <UserX size={16} />
                    </button>
                ) : (
                    <button
                        type="button"
                        className="emp-iconbtn emp-status-btn react"
                        title="Reactivate employee"
                        onClick={() => setStatusConfirm({ emp, next: true })}
                    >
                        <UserCheck size={16} />
                    </button>
                ))}
        </div>
    );

    const renderLastLogin = (emp: EmployeeInfo) => {
        const full = applyOffsetToUTCDateTime(emp.lastLoginDate, emp.lastLoginDateOffset);
        if (!full || full === "-") return <span className="c-main">{DASH}</span>;
        const spaceIdx = full.indexOf(" ");
        const datePart = spaceIdx === -1 ? full : full.slice(0, spaceIdx);
        const timePart = spaceIdx === -1 ? "" : full.slice(spaceIdx + 1);
        return (
            <>
                <span className="c-main emp-mono">{datePart}</span>
                {timePart && <span className="c-sub emp-mono">{timePart}</span>}
            </>
        );
    };

    // sortable header cell
    const Th: React.FC<{ label: string; sk?: SortKey; center?: boolean; help?: string }> = ({
        label,
        sk,
        center,
        help,
    }) => {
        const sortable = !!sk;
        const sorted = sortable && sortKey === sk;
        return (
            <th
                className={`${center ? "cen" : ""} ${sortable ? "sortable" : ""} ${sorted ? "sorted" : ""}`}
                onClick={sortable ? () => handleSort(sk!) : undefined}
            >
                <span className="th-in">
                    {label}
                    {sortable &&
                        (sorted ? (
                            sortDirection === "desc" ? (
                                <ChevronDown className="caret" size={13} />
                            ) : (
                                <ChevronUp className="caret" size={13} />
                            )
                        ) : (
                            <ChevronUp className="caret" size={13} />
                        ))}
                    {help && (
                        <span className="th-help" data-tip={help}>
                            <Info size={13} />
                        </span>
                    )}
                </span>
            </th>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    const activeChips =
        selectedLocation !== "ALL" ||
        selectedSuper !== "ALL" ||
        (showDrive && driveFilter !== "all") ||
        (showPhone && phoneFilter !== "all") ||
        (showWorkOrders && woFilter !== "all") ||
        (showWorkOrders && woRateAuditOnly) ||
        (showTimeOff && timeOffFilter !== "all") ||
        (showPhoto && photoFilter !== "all") ||
        dupPhonesOnly ||
        searchTerm.trim() !== "";

    return (
        <PageWrapper title={translator.translate("EmployeeView")} backgroundColor="#f4f5f7">
            <div className="emp">
                {/* breadcrumb */}
                <div className="emp-crumbs">
                    <span>People</span>
                    <ChevronRight />
                    <span className="here">Employees</span>
                </div>

                {/* header */}
                <div className="emp-app-head">
                    <div className="emp-logo">
                        <Users size={23} />
                    </div>
                    <div>
                        <h1 className="emp-app-title">Employees</h1>
                        <div className="emp-app-sub">
                            The company roster. Search, filter, and sort every employee; edit role, PW classification,
                            drive-time access, and enrollment photos; and sync new hires from the source system.
                        </div>
                    </div>
                </div>

                {/* filter / control bar */}
                <div className="emp-toolbar">
                    {showLocationFilter && (
                        <div className="emp-field">
                            <span className="flab">Location</span>
                            <select
                                className="emp-select"
                                value={selectedLocation}
                                onChange={(e) => setSelectedLocation(e.target.value)}
                            >
                                <option value="ALL">All locations</option>
                                {locations
                                    ?.filter((l) => l.testLocation === false)
                                    .sort((a, b) => {
                                        const ak = a.altLocationID || a.name;
                                        const bk = b.altLocationID || b.name;
                                        return ak.localeCompare(bk);
                                    })
                                    .map((l) => (
                                        <option key={l.locationID} value={String(l.locationID)}>
                                            {l.altLocationID ? `${l.altLocationID} — ${l.name}` : l.name}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    )}

                    {/* A type-to-filter picker rather than a native select: WCDP runs ~66
                        supervisors and scrolling to one is slow. The option text already
                        leads with the EE# for every tenant, and FloatMenuButton filters on
                        that text, so typing a number or a name both narrow the list.
                        Carries its own label, like the review board's filter pickers. */}
                    <div className="emp-field emp-superpick">
                        <FloatMenuButton
                            label="Supervisor"
                            valueLabel={supervisorMenuOptions.find((o) => o.value === selectedSuper)?.label
                                ?? 'All supervisors'}
                            isPlaceholder={selectedSuper === 'ALL'}
                            isActive={selectedSuper !== 'ALL'}
                            options={supervisorMenuOptions}
                            selectedValue={selectedSuper}
                            onSelect={setSelectedSuper}
                            searchable
                        />
                    </div>

                    <div className="emp-field">
                        <span className="flab">Status</span>
                        <div className="emp-segmented" role="tablist">
                            <button
                                type="button"
                                className={isActive && !dupPhonesOnly ? "on" : ""}
                                onClick={() => {
                                    setIsActive(true);
                                    setDupPhonesOnly(false);
                                }}
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                className={!isActive && !dupPhonesOnly ? "on" : ""}
                                onClick={() => {
                                    setIsActive(false);
                                    setDupPhonesOnly(false);
                                }}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>

                    {showDrive && (
                        <div className="emp-field">
                            <span className="flab">Drive times</span>
                            <select
                                className="emp-select"
                                value={driveFilter}
                                onChange={(e) => setDriveFilter(e.target.value as "all" | "on" | "off")}
                            >
                                <option value="all">All</option>
                                <option value="on">Enabled</option>
                                <option value="off">Disabled</option>
                            </select>
                        </div>
                    )}

                    {showPhone && (
                        <div className="emp-field">
                            <span className="flab">Company phone</span>
                            <select
                                className="emp-select"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value as "all" | "on" | "off")}
                            >
                                <option value="all">All</option>
                                <option value="on">Has company phone</option>
                                <option value="off">No company phone</option>
                            </select>
                        </div>
                    )}

                    {showWorkOrders && (
                        <div className="emp-field">
                            <span className="flab">Work orders</span>
                            <select
                                className="emp-select"
                                value={woFilter}
                                onChange={(e) => setWoFilter(e.target.value as "all" | "on" | "off")}
                            >
                                <option value="all">All</option>
                                <option value="on">Enabled</option>
                                <option value="off">Disabled</option>
                            </select>
                        </div>
                    )}

                    {showWorkOrders && (
                        <div className="emp-field">
                            <span className="flab">WO rate audit</span>
                            <div className="emp-segmented" role="tablist">
                                <button
                                    type="button"
                                    className={!woRateAuditOnly ? "on" : ""}
                                    onClick={() => setWoRateAuditOnly(false)}
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    className={woRateAuditOnly ? "on" : ""}
                                    onClick={() => setWoRateAuditOnly(true)}
                                >
                                    WO rate ≠ system
                                </button>
                            </div>
                        </div>
                    )}

                    {showTimeOff && (
                        <div className="emp-field">
                            <span className="flab">Time off policy</span>
                            <select
                                className="emp-select"
                                value={timeOffFilter}
                                onChange={(e) => setTimeOffFilter(e.target.value)}
                            >
                                <option value="all">All</option>
                                {timeOffPolicyOptions.map((o) => (
                                    <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                                <option value="unassigned">Unassigned</option>
                            </select>
                        </div>
                    )}

                    {showPhoto && (
                        <div className="emp-field">
                            <span className="flab">Photo</span>
                            <select
                                className="emp-select"
                                value={photoFilter}
                                onChange={(e) => setPhotoFilter(e.target.value as PhotoFilter)}
                            >
                                <option value="all">All</option>
                                <option value="enrolled">On file</option>
                                <option value="verifying">Verifying</option>
                                <option value="failed">Failed</option>
                                <option value="none">Needed</option>
                            </select>
                        </div>
                    )}

                    <div className="emp-field">
                        <span className="flab">Sort by</span>
                        <div className="emp-sortby">
                            <select
                                className="emp-select"
                                value={sortKey}
                                onChange={(e) => setSortKey(e.target.value as SortKey)}
                            >
                                {sortOptions.map((k) => (
                                    <option key={k} value={k}>
                                        {SORT_LABELS[k]}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="emp-btn emp-dir-btn"
                                title={sortDirection === "asc" ? "Ascending" : "Descending"}
                                onClick={() => setSortDirection((d) => (d === "asc" ? "desc" : "asc"))}
                            >
                                {sortDirection === "asc" ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
                            </button>
                        </div>
                    </div>

                    <div className="emp-field search">
                        <span className="flab">Search</span>
                        <div className="emp-searchwrap">
                            <Search size={17} className="emp-search-icon" />
                            <input
                                className="emp-input"
                                placeholder="Search by name, employee ID, or phone…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <button type="button" className="emp-btn primary emp-sync-btn" onClick={() => setIsSyncModalOpen(true)}>
                        <RefreshCw size={16} /> Sync New Employee
                    </button>
                </div>

                {/* result line + chips */}
                <div className="emp-result-line">
                    <span>
                        <b>{total}</b> {total === 1 ? "employee" : "employees"} ·{" "}
                        <b>{dupPhonesOnly ? "Active + Inactive" : isActive ? "Active" : "Inactive"}</b>
                    </span>
                    {dupPhonesOnly && (
                        <span className="rl-chip">
                            Duplicate phones
                            <button className="rl-x" title="Clear" onClick={() => setDupPhonesOnly(false)}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {selectedLocation !== "ALL" && (
                        <span className="rl-chip">
                            Location: {locationChipLabel(selectedLocation)}
                            <button className="rl-x" title="Clear" onClick={() => setSelectedLocation("ALL")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {selectedSuper !== "ALL" && (
                        <span className="rl-chip">
                            Supervisor: {supervisorChipLabel(selectedSuper)}
                            <button className="rl-x" title="Clear" onClick={() => setSelectedSuper("ALL")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {showDrive && driveFilter !== "all" && (
                        <span className="rl-chip">
                            Drive times: {driveFilter === "on" ? "Enabled" : "Disabled"}
                            <button className="rl-x" title="Clear" onClick={() => setDriveFilter("all")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {showPhone && phoneFilter !== "all" && (
                        <span className="rl-chip">
                            Company phone: {phoneFilter === "on" ? "Yes" : "No"}
                            <button className="rl-x" title="Clear" onClick={() => setPhoneFilter("all")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {showWorkOrders && woFilter !== "all" && (
                        <span className="rl-chip">
                            Work orders: {woFilter === "on" ? "Enabled" : "Disabled"}
                            <button className="rl-x" title="Clear" onClick={() => setWoFilter("all")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {showWorkOrders && woRateAuditOnly && (
                        <span className="rl-chip">
                            WO rate ≠ system
                            <button className="rl-x" title="Clear" onClick={() => setWoRateAuditOnly(false)}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {showTimeOff && timeOffFilter !== "all" && (
                        <span className="rl-chip">
                            Time off policy: {timeOffChipLabel(timeOffFilter)}
                            <button className="rl-x" title="Clear" onClick={() => setTimeOffFilter("all")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {showPhoto && photoFilter !== "all" && (
                        <span className="rl-chip">
                            Photo: {PHOTO_STATE_LABEL[photoFilter]}
                            <button className="rl-x" title="Clear" onClick={() => setPhotoFilter("all")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {searchTerm.trim() !== "" && (
                        <span className="rl-chip">
                            “{searchTerm.trim()}”
                            <button className="rl-x" title="Clear" onClick={() => setSearchTerm("")}>
                                <X size={13} />
                            </button>
                        </span>
                    )}
                    {/* photo-status freshness (Q9) — the poll runs only while a row in scope is verifying */}
                    {showPhoto && data && (
                        <span className="rl-fresh">
                            {verifyingInScope > 0 && (
                                <span className="rl-poll">
                                    <EnmSpin />
                                    {verifyingInScope} verifying · checking every minute
                                </span>
                            )}
                            {photoStatusAt && (
                                <span>
                                    Photo status <span className="emp-mono">{fmtTime(photoStatusAt.toISOString())}</span>
                                </span>
                            )}
                            <button
                                type="button"
                                className="rl-refresh"
                                disabled={refreshingPhotos}
                                onClick={() => refreshPhotoStatus(true)}
                            >
                                {refreshingPhotos ? <EnmSpin /> : <RefreshCw size={13} />}
                                {refreshingPhotos ? "Refreshing" : "Refresh"}
                            </button>
                        </span>
                    )}
                </div>

                {/* duplicate-phone callout — whole roster (active + inactive), ignores every filter */}
                {data && !dupBannerDismissed && dupPhones.size > 0 && (
                    <div className="emp-ph-banner emp-dup-banner" role="status">
                        <PhoneCall size={20} />
                        <div className="ph-b-body">
                            <div className="ph-b-t">
                                <b>{dupPhones.size}</b> {dupPhones.size === 1 ? "phone number is" : "phone numbers are"} on
                                more than one employee record
                            </div>
                            <div className="ph-b-s">
                                Shared numbers can stop employees from signing in to the mobile app. Each number should
                                belong to only one employee, active or inactive.
                            </div>
                        </div>
                        <div className="ph-b-acts">
                            <button
                                type="button"
                                className="emp-btn sm primary emp-dup-toggle"
                                onClick={() => setDupPhonesOnly((v) => !v)}
                            >
                                {dupPhonesOnly ? <X size={15} /> : <Layers size={15} />}
                                {dupPhonesOnly ? "Back to list" : "Show duplicates"}
                            </button>
                        </div>
                        <button type="button" className="ph-b-x" title="Hide" onClick={() => setDupBannerDismissed(true)}>
                            <X size={15} />
                        </button>
                    </div>
                )}

                {/* enrollment-photo coverage callout — scope ignores the Photo filter itself */}
                {showPhoto && data && !photoBannerDismissed && photoGap > 0 && (
                    <div className="emp-ph-banner" role="status">
                        <Camera size={20} />
                        <div className="ph-b-body">
                            <div className="ph-b-t">
                                <b>{photoGap}</b> of {scopeRows.length} {dupPhonesOnly ? "listed" : isActive ? "active" : "inactive"} employees can’t
                                face-match clock in yet
                            </div>
                            <div className="ph-b-s">
                                {coveragePct}% verified in this view. Photos that are still verifying count once Azure
                                accepts them, usually within 15 minutes.
                            </div>
                            {/* each count IS the Photo filter; the active one goes dark with a × */}
                            <div className="ph-b-counts">
                                {PHOTO_COUNT_CHIPS.filter(([k]) => photoCounts[k] > 0).map(([k, label]) => {
                                    const on = photoFilter === k;
                                    return (
                                        <button
                                            key={k}
                                            type="button"
                                            className={`ph-cnt k-${k}${on ? " on" : ""}`}
                                            title={on ? "Show everyone" : "Show only these"}
                                            onClick={() => setPhotoFilter(on ? "all" : k)}
                                        >
                                            <span className="dot" />
                                            <b>{photoCounts[k]}</b>
                                            {label}
                                            {on && <X />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="ph-b-acts">
                            <button
                                type="button"
                                className="emp-btn sm primary"
                                disabled={photoCounts.none + photoCounts.failed === 0}
                                onClick={() => setNeedingPhotosOpen(true)}
                            >
                                <Layers size={15} /> Get the list
                            </button>
                        </div>
                        <button
                            type="button"
                            className="ph-b-x"
                            title="Hide"
                            onClick={() => setPhotoBannerDismissed(true)}
                        >
                            <X size={15} />
                        </button>
                    </div>
                )}

                {/* table */}
                <div className="emp-card">
                    <div className="emp-tbl-wrap">
                        <table className="emp-dt">
                            <thead>
                                <tr>
                                    <Th label="Employee" sk="Name" />
                                    <Th label="Role & Title" sk="Role" />
                                    <Th label="Reports To" sk="Supervisor" />
                                    <Th label="Last Login" sk="LastLogin" />
                                    {showPW && <Th label="PW Classification" sk="PWClassification" />}
                                    {showTimeOff && <Th label="Time Off Policy" sk="TimeOffPolicy" help={TO_HELP} />}
                                    {showDrive && <Th label="Drive times" sk="DriveTime" center help={DRIVE_HELP} />}
                                    {showPhone && <Th label="Company phone" sk="CompanyPhone" center help={PHONE_HELP} />}
                                    {showWorkOrders && <Th label="Work orders" sk="WorkOrders" center help={WO_HELP} />}
                                    {showWorkOrders && <Th label="WO Rate" sk="WORate" help={WO_RATE_HELP} />}
                                    {showPhoto && <Th label="Photo" sk="Photo" center help={PHOTO_HELP} />}
                                    <Th label="Actions" center />
                                </tr>
                            </thead>
                            <tbody>
                                {data && total === 0 && (
                                    <tr>
                                        <td colSpan={visibleColCount}>
                                            <div className="emp-empty">
                                                <div className="eic">
                                                    <Users size={26} />
                                                </div>
                                                <div className="et">No employees match these filters</div>
                                                <p>
                                                    Try clearing the search or switching the location, supervisor, drive
                                                    times, or active filters.
                                                </p>
                                                {activeChips && (
                                                    <button type="button" className="emp-btn sm" onClick={clearFilters}>
                                                        <X size={15} /> Clear filters
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                {pageRows.map((emp) => {
                                    const phone = formatPhoneNumber(emp.phoneNumber);
                                    const idSub = `${emp.altEmployeeID || DASH}${phone ? ` · ${phone}` : ""}`;
                                    const dup = dupGroupOf(emp);
                                    return (
                                        <tr key={emp.employeeID} className={emp.active ? "" : "inactive-row"}>
                                            {/* Employee */}
                                            <td>
                                                <div className="who-cell">
                                                    <span className={`av${showPhoto ? AVATAR_CUE[enrollmentState(emp)] : ""}`}>
                                                        {initials(emp.firstName, emp.lastName)}
                                                    </span>
                                                    <div>
                                                        <div className="nm">
                                                            {emp.firstName}
                                                            {emp.middleName ? (
                                                                <span className="mid"> {emp.middleName}</span>
                                                            ) : null}{" "}
                                                            {emp.lastName}
                                                        </div>
                                                        <div className="who-sub emp-mono">{idSub}</div>
                                                        {dup && (
                                                            <div className="who-dup">
                                                                <span
                                                                    className="emp-dup-tag"
                                                                    title={`This number is on ${dup.count} employee records (${dup.activeCount} active)`}
                                                                >
                                                                    Duplicate phone
                                                                </span>
                                                                {dupPhonesOnly && !emp.active && (
                                                                    <span className="emp-dup-tag off">Inactive</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Role & Title */}
                                            <td>
                                                <div className="cell-2">
                                                    <div className="c-main">{renderRoleCell(emp)}</div>
                                                    <div className="c-sub">{emp.jobTitle || DASH}</div>
                                                    <div className="c-sub" title="Hire date (from the ERP sync — read only)">
                                                        Hire date: {fmtISO(emp.hireDate)}
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Reports To */}
                                            <td>
                                                <div className="cell-2">
                                                    <div className="c-main">{getSupervisorName(emp.supervisorID)}</div>
                                                    <div className="c-sub">{getLocationName(emp.locationID)}</div>
                                                </div>
                                            </td>
                                            {/* Last Login */}
                                            <td>
                                                <div className="cell-2">{renderLastLogin(emp)}</div>
                                            </td>
                                            {/* PW */}
                                            {showPW && <td>{renderPWCell(emp)}</td>}
                                            {/* Time Off Policy */}
                                            {showTimeOff && <td>{renderTimeOffCell(emp)}</td>}
                                            {/* Drive times */}
                                            {showDrive && <td className="cen">{renderDriveCell(emp)}</td>}
                                            {/* Company phone */}
                                            {showPhone && <td className="cen">{renderPhoneCell(emp)}</td>}
                                            {/* Work orders */}
                                            {showWorkOrders && <td className="cen">{renderWOCell(emp)}</td>}
                                            {/* WO Rate */}
                                            {showWorkOrders && <td>{renderWORateCell(emp)}</td>}
                                            {/* Photo status */}
                                            {showPhoto && <td className="cen">{renderPhotoCell(emp)}</td>}
                                            {/* Actions */}
                                            <td className="cen">{renderActionsCell(emp)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* pagination */}
                {total > 0 && (
                    <div className="emp-pager">
                        <span className="count">
                            Showing <b>{start + 1}–{start + pageRows.length}</b> of <b>{total}</b>
                        </span>
                        <span className="psize">
                            Rows
                            <select
                                className="emp-select"
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
                            >
                                {PAGE_SIZES.map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))}
                            </select>
                        </span>
                        <span className="pages">
                            <button className="pg nav" disabled={page === 1} onClick={() => goPage(page - 1)}>
                                <ChevronLeft size={16} />
                            </button>
                            {pageWindow(page, totalPages, 7).map((n, i) =>
                                n === "…" ? (
                                    <span key={`e${i}`} className="pg ellipsis">
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={n}
                                        className={`pg ${n === page ? "on" : ""}`}
                                        onClick={() => goPage(n)}
                                    >
                                        {n}
                                    </button>
                                )
                            )}
                            <button className="pg nav" disabled={page === totalPages} onClick={() => goPage(page + 1)}>
                                <ChevronRight size={16} />
                            </button>
                        </span>
                    </div>
                )}

                {/* time-off policy assignment modal */}
                {toEmployee && (
                    <div className="emp-modal-bg" onClick={closeTimeOffModal}>
                        <div
                            className="emp-modal emp-modal-wide"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="emp-modal-head">
                                <div className="mi">
                                    <Calendar size={21} />
                                </div>
                                <div>
                                    <h3>Time Off Policy</h3>
                                    <div className="ms">
                                        {toEmployee.firstName} {toEmployee.lastName} · {toEmployee.altEmployeeID || DASH}
                                    </div>
                                </div>
                                <button type="button" className="x" onClick={closeTimeOffModal}>
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="emp-modal-body">
                                {/* Identity card */}
                                <div className="emp-confirm-emp">
                                    <span className="av">
                                        {initials(toEmployee.firstName, toEmployee.lastName)}
                                    </span>
                                    <div>
                                        <div className="nm">
                                            {toEmployee.firstName} {toEmployee.lastName}
                                        </div>
                                        <div className="who-sub emp-mono">
                                            {toEmployee.altEmployeeID} · {getRoleDisplay(toEmployee.roleID)}
                                        </div>
                                    </div>
                                </div>

                                {toLoading && (
                                    <div className="emp-to-loading">
                                        <span className="emp-spin" /> Loading policy data…
                                    </div>
                                )}

                                {!toLoading && toDataFetched && (
                                    <>
                                        {/* Tab bar: Balances · Activity · Assignment */}
                                        <div className="emp-to-tabs" role="tablist">
                                            {(
                                                [
                                                    ["balances", "Balances"],
                                                    ["activity", "Activity"],
                                                    ["assignment", "Assignment"],
                                                ] as [ToTab, string][]
                                            ).map(([t, label]) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={toTab === t}
                                                    className={`emp-to-tab${toTab === t ? " on" : ""}`}
                                                    onClick={() => setToTab(t)}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Fixed-height pane — switching tabs never resizes the modal */}
                                        <div className="emp-to-pane">
                                            {toTab === "balances" && renderToBalancesPane()}
                                            {toTab === "activity" && renderToActivityPane()}
                                            {toTab === "assignment" && (
                                                <>
                                        {/* Current assignment */}
                                        <div className="emp-to-subhead" style={{ marginTop: 4 }}>Current assignment</div>
                                        {toCurrent ? (
                                            <div className="emp-to-cur-card">
                                                <div className="emp-to-cur-top">
                                                    <span className="emp-to-cur-ic">
                                                        <Calendar size={18} />
                                                    </span>
                                                    <div className="emp-to-cur-body">
                                                        <div className="emp-to-cur-name">{toCurrent.policyName}</div>
                                                        <div className="emp-to-cur-meta">
                                                            <span className="emp-to-cur-since">
                                                                <Clock size={13} />
                                                                Since {fmtISO(toCurrent.startDate)}
                                                            </span>
                                                            {toCurrent.assignedByAdminEmployeeID && (
                                                                <span>
                                                                    Assigned by{" "}
                                                                    {adminNames?.get(toCurrent.assignedByAdminEmployeeID) ??
                                                                        `admin #${toCurrent.assignedByAdminEmployeeID}`}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <span className="emp-cur-badge">
                                                        <span className="dot" />
                                                        Current
                                                    </span>
                                                </div>
                                                {toCurrent.notes && (
                                                    <div className="emp-to-cur-notes">{toCurrent.notes}</div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="emp-to-empty">
                                                <AlertTriangle size={19} />
                                                <div>
                                                    <div className="emp-to-empty-t">No time off policy assigned</div>
                                                    <div className="emp-to-empty-s">
                                                        This employee can&apos;t accrue or request time off in the mobile app until a policy is assigned below.
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* History timeline (only when ≥1 prior record in history array) */}
                                        {toHistory.length > 0 && (
                                            <>
                                                <div className="emp-to-divider" />
                                                <div className="emp-to-subhead">Assignment history</div>
                                                <div className="emp-to-timeline">
                                                    {/* Newest first — current record first, then history */}
                                                    {[...(toCurrent ? [toCurrent] : []), ...toHistory]
                                                        .slice(0, toCurrent ? undefined : undefined)
                                                        .map((item) => {
                                                            const open = item.endDate === null;
                                                            return (
                                                                <div
                                                                    key={item.employeeTimeOffPolicyID}
                                                                    className={`emp-to-tl-item${open ? " cur" : ""}`}
                                                                >
                                                                    <span className="emp-to-tl-dot" />
                                                                    <div className="emp-to-tl-body">
                                                                        <div className="emp-to-tl-head">
                                                                            <span className="emp-to-tl-name">{item.policyName}</span>
                                                                            {open && (
                                                                                <span className="emp-cur-badge">
                                                                                    <span className="dot" />Current
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="emp-to-tl-range emp-mono">
                                                                            {fmtISO(item.startDate)}
                                                                            {" → "}
                                                                            {open ? (
                                                                                <span className="emp-to-tl-present">Present</span>
                                                                            ) : (
                                                                                fmtISO(item.endDate)
                                                                            )}
                                                                        </div>
                                                                        {item.notes && (
                                                                            <div className="emp-to-tl-notes">{item.notes}</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </>
                                        )}

                                        {/* Assign / Change form (edit permission only) */}
                                        {canEdit ? (
                                            <>
                                                <div className="emp-to-divider" />
                                                <div className="emp-to-subhead">
                                                    {toCurrent ? "Change policy" : "Assign policy"}
                                                </div>

                                                {/* Policy select */}
                                                <div className="emp-to-frow">
                                                    <label>
                                                        Time off policy <span className="emp-req">*</span>
                                                    </label>
                                                    <select
                                                        className="emp-select"
                                                        value={toSelectedPolicyID}
                                                        onChange={(e) => setToSelectedPolicyID(Number(e.target.value))}
                                                    >
                                                        {toActivePolicies.map((p) => (
                                                            <option key={p.timeOffPolicyID} value={p.timeOffPolicyID}>
                                                                {p.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {toActivePolicies.find((p) => p.timeOffPolicyID === toSelectedPolicyID)?.description && (
                                                        <div className="emp-to-help">
                                                            {toActivePolicies.find((p) => p.timeOffPolicyID === toSelectedPolicyID)!.description}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Date + Notes side-by-side */}
                                                <div className="emp-to-frow2">
                                                    <div className="emp-to-frow" style={{ marginBottom: 0 }}>
                                                        <label>
                                                            Effective start date <span className="emp-req">*</span>
                                                        </label>
                                                        <input
                                                            type="date"
                                                            className={`emp-input${toDateError ? " emp-to-err" : ""}`}
                                                            value={toStartDate}
                                                            onChange={(e) => {
                                                                setToStartDate(e.target.value);
                                                                if (e.target.value) setToDateError(false);
                                                            }}
                                                        />
                                                        {toDateError && (
                                                            <div className="emp-to-inline-err">
                                                                <AlertTriangle size={15} />
                                                                A start date is required.
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="emp-to-frow" style={{ marginBottom: 0 }}>
                                                        <label>
                                                            Notes <span className="emp-to-opt">(optional)</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="emp-input"
                                                            placeholder="e.g. Promoted to salaried"
                                                            value={toNotes}
                                                            onChange={(e) => setToNotes(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Non-blocking backdate warning */}
                                                {toCurrent && toStartDate && toStartDate < toCurrent.startDate && (
                                                    <div className="emp-to-warn-banner">
                                                        <AlertTriangle size={17} />
                                                        <div id="toBackdateMsg">
                                                            This date is <b>before</b> the current assignment began ({fmtISO(toCurrent.startDate)}).
                                                            Backdating can retroactively change accrual for a closed period — double-check the effective date.
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Consequence line */}
                                                <div className="emp-to-preview-line">
                                                    <Info size={16} />
                                                    <div>
                                                        Changing the policy affects <b>future accrual</b> from the effective date
                                                        onward — already-earned balances are not recalculated.
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="emp-to-divider" />
                                                <div className="emp-to-ro-note">
                                                    <Info size={16} />
                                                    You don&apos;t have permission to change time off policies. Contact an administrator to make changes.
                                                </div>
                                            </>
                                        )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Footer — tab-aware: Cancel + Save/Assign on the (editable)
                                Assignment tab, a single Close on the read-only tabs. */}
                            <div className="emp-modal-foot">
                                {toTab === "assignment" && canEdit && toDataFetched ? (
                                    <>
                                        <button type="button" className="emp-btn" onClick={closeTimeOffModal}>
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="emp-btn primary"
                                            disabled={toSaving || !toSelectedPolicyID}
                                            onClick={handleSaveTimeOffPolicy}
                                        >
                                            {toSaving ? (
                                                <><span className="emp-spin" /> Saving…</>
                                            ) : (
                                                <><Check size={16} />{toCurrent ? "Save policy" : "Assign policy"}</>
                                            )}
                                        </button>
                                    </>
                                ) : (
                                    <button type="button" className="emp-btn" onClick={closeTimeOffModal}>
                                        Close
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* drive-time confirm modal */}
                {driveConfirm && (
                    <div className="emp-modal-bg" onClick={() => setDriveConfirm(null)}>
                        <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="emp-modal-head">
                                <div className="mi">
                                    <Truck size={21} />
                                </div>
                                <div>
                                    <h3>{driveConfirm.next ? "Allow drive time?" : "Disable drive time?"}</h3>
                                    <div className="ms">
                                        {driveConfirm.next
                                            ? "Enable drive-time logging in the mobile app"
                                            : "Remove drive-time logging in the mobile app"}
                                    </div>
                                </div>
                                <button type="button" className="x" onClick={() => setDriveConfirm(null)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="emp-modal-body">
                                <div className="emp-confirm-emp">
                                    <span className="av">
                                        {initials(driveConfirm.emp.firstName, driveConfirm.emp.lastName)}
                                    </span>
                                    <div>
                                        <div className="nm">
                                            {driveConfirm.emp.firstName} {driveConfirm.emp.lastName}
                                        </div>
                                        <div className="who-sub emp-mono">
                                            {driveConfirm.emp.altEmployeeID} · {getRoleDisplay(driveConfirm.emp.roleID)}
                                        </div>
                                    </div>
                                </div>
                                <div className="emp-dt-explain">
                                    <Info size={16} />
                                    <div>
                                        {driveConfirm.next ? (
                                            <>
                                                <b>{driveConfirm.emp.firstName}</b> will be able to log drive time at the
                                                beginning and end of the day on their next mobile app sync.
                                            </>
                                        ) : (
                                            <>
                                                <b>{driveConfirm.emp.firstName}</b> will no longer see the “add drive time”
                                                option in the mobile app. Existing entries are unaffected.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="emp-modal-foot">
                                <button type="button" className="emp-btn" onClick={() => setDriveConfirm(null)}>
                                    Cancel
                                </button>
                                <button type="button" className="emp-btn primary" onClick={confirmDrive}>
                                    <Check size={16} />
                                    {driveConfirm.next ? "Allow drive time" : "Disable drive time"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* company-phone confirm modal */}
                {phoneConfirm && (
                    <div className="emp-modal-bg" onClick={() => setPhoneConfirm(null)}>
                        <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="emp-modal-head">
                                <div className="mi">
                                    <Info size={21} />
                                </div>
                                <div>
                                    <h3>{phoneConfirm.next ? "Mark as having a company phone?" : "Remove company phone?"}</h3>
                                    <div className="ms">
                                        {phoneConfirm.next
                                            ? "Stops phone reimbursement"
                                            : "Resumes phone reimbursement"}
                                    </div>
                                </div>
                                <button type="button" className="x" onClick={() => setPhoneConfirm(null)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="emp-modal-body">
                                <div className="emp-confirm-emp">
                                    <span className="av">
                                        {initials(phoneConfirm.emp.firstName, phoneConfirm.emp.lastName)}
                                    </span>
                                    <div>
                                        <div className="nm">
                                            {phoneConfirm.emp.firstName} {phoneConfirm.emp.lastName}
                                        </div>
                                        <div className="who-sub emp-mono">
                                            {phoneConfirm.emp.altEmployeeID} · {getRoleDisplay(phoneConfirm.emp.roleID)}
                                        </div>
                                    </div>
                                </div>
                                {phoneConfirm.next && (
                                    <div className="emp-field" style={{ marginBottom: 12 }}>
                                        <label className="flab" htmlFor="emp-phone-date">
                                            Phone issued on
                                        </label>
                                        <input
                                            id="emp-phone-date"
                                            type="date"
                                            className="emp-select"
                                            required
                                            value={phoneDate}
                                            onChange={(e) => setPhoneDate(e.target.value)}
                                        />
                                    </div>
                                )}
                                <div className="emp-dt-explain">
                                    <Info size={16} />
                                    <div>
                                        {phoneConfirm.next
                                            ? "Phone reimbursement stops for pay periods starting on or after this date."
                                            : "Employee will receive phone reimbursement again starting with the next processed timesheet."}
                                    </div>
                                </div>
                            </div>
                            <div className="emp-modal-foot">
                                <button type="button" className="emp-btn" onClick={() => setPhoneConfirm(null)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="emp-btn primary"
                                    onClick={confirmPhone}
                                    disabled={phoneConfirm.next && !phoneDate}
                                >
                                    <Check size={16} />
                                    {phoneConfirm.next ? "Mark as company phone" : "Remove company phone"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* activate / inactivate confirm modal */}
                {statusConfirm && (
                    <div className="emp-modal-bg" onClick={() => setStatusConfirm(null)}>
                        <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                            <div className={`emp-modal-head${statusConfirm.next ? "" : " danger"}`}>
                                <div className="mi">
                                    {statusConfirm.next ? <UserCheck size={21} /> : <UserX size={21} />}
                                </div>
                                <div>
                                    <h3>{statusConfirm.next ? "Reactivate employee?" : "Deactivate employee?"}</h3>
                                    <div className="ms">
                                        {statusConfirm.next
                                            ? "Restore access and return to the Active roster"
                                            : "Revoke mobile app access and move to the Inactive roster"}
                                    </div>
                                </div>
                                <button type="button" className="x" onClick={() => setStatusConfirm(null)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="emp-modal-body">
                                <div className="emp-confirm-emp">
                                    <span className="av">
                                        {initials(statusConfirm.emp.firstName, statusConfirm.emp.lastName)}
                                    </span>
                                    <div>
                                        <div className="nm">
                                            {statusConfirm.emp.firstName} {statusConfirm.emp.lastName}
                                        </div>
                                        <div className="who-sub emp-mono">
                                            {statusConfirm.emp.altEmployeeID} · {getRoleDisplay(statusConfirm.emp.roleID)}
                                        </div>
                                    </div>
                                </div>
                                <div className={`emp-dt-explain${statusConfirm.next ? "" : " warn"}`}>
                                    {statusConfirm.next ? <Info size={16} /> : <AlertTriangle size={16} />}
                                    <div>
                                        {statusConfirm.next ? (
                                            <>
                                                <b>
                                                    {statusConfirm.emp.firstName} {statusConfirm.emp.lastName}
                                                </b>{" "}
                                                will regain mobile app access and return to the <b>Active</b> roster with
                                                their previous role and settings intact.
                                            </>
                                        ) : (
                                            <>
                                                <b>
                                                    {statusConfirm.emp.firstName} {statusConfirm.emp.lastName}
                                                </b>{" "}
                                                will no longer be able to sign in or log time in the mobile app and moves
                                                to the <b>Inactive</b> roster. Their timesheets, history, and records are
                                                retained and can be restored anytime.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="emp-modal-foot">
                                <button type="button" className="emp-btn" onClick={() => setStatusConfirm(null)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={`emp-btn ${statusConfirm.next ? "primary" : "danger-solid"}`}
                                    onClick={confirmStatus}
                                >
                                    {statusConfirm.next ? <UserCheck size={16} /> : <UserX size={16} />}
                                    {statusConfirm.next ? "Reactivate" : "Deactivate"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* work-order-creation confirm modal */}
                {woConfirm && (
                    <div className="emp-modal-bg" onClick={() => setWoConfirm(null)}>
                        <div className="emp-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="emp-modal-head">
                                <div className="mi">
                                    <ClipboardList size={21} />
                                </div>
                                <div>
                                    <h3>{woConfirm.next ? "Allow work order creation?" : "Disable work order creation?"}</h3>
                                    <div className="ms">
                                        {woConfirm.next
                                            ? "Enable Work Order creation in the mobile app"
                                            : "Remove Work Order creation in the mobile app"}
                                    </div>
                                </div>
                                <button type="button" className="x" onClick={() => setWoConfirm(null)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="emp-modal-body">
                                <div className="emp-confirm-emp">
                                    <span className="av">
                                        {initials(woConfirm.emp.firstName, woConfirm.emp.lastName)}
                                    </span>
                                    <div>
                                        <div className="nm">
                                            {woConfirm.emp.firstName} {woConfirm.emp.lastName}
                                        </div>
                                        <div className="who-sub emp-mono">
                                            {woConfirm.emp.altEmployeeID} · {getRoleDisplay(woConfirm.emp.roleID)}
                                        </div>
                                    </div>
                                </div>
                                <div className="emp-dt-explain">
                                    <Info size={16} />
                                    <div>
                                        {woConfirm.next ? (
                                            <>
                                                <b>{woConfirm.emp.firstName}</b> will be able to create Work Orders in the
                                                mobile app on their next sync.
                                            </>
                                        ) : (
                                            <>
                                                <b>{woConfirm.emp.firstName}</b> will no longer see the “new work order”
                                                option in the mobile app. Existing work orders are unaffected.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="emp-modal-foot">
                                <button type="button" className="emp-btn" onClick={() => setWoConfirm(null)}>
                                    Cancel
                                </button>
                                <button type="button" className="emp-btn primary" onClick={confirmWO}>
                                    <Check size={16} />
                                    {woConfirm.next ? "Allow work orders" : "Disable work orders"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* v2 modals — must render INSIDE .emp: their classes depend on the
                    scope's CSS variables, box-sizing reset, and font inheritance. */}
                {roleEditEmployee && (
                    <EmployeeRoleEditModal
                        isOpen={!!roleEditEmployee}
                        employee={roleEditEmployee}
                        roles={activeEmployeeRoles}
                        onClose={() => setRoleEditEmployee(null)}
                        onSave={handleRoleSave}
                    />
                )}

                <EmployeeSyncModalV2
                    isOpen={isSyncModalOpen}
                    onClose={() => setIsSyncModalOpen(false)}
                    onSubmit={handleEmployeeSync}
                    config={config}
                />

                {pwClassificationModalEmployee && (
                    <EmployeePWClassificationModal
                        isOpen={true}
                        employee={pwClassificationModalEmployee}
                        onClose={() => setPWClassificationModalEmployee(null)}
                        onSaved={async () => {
                            setPWClassificationModalEmployee(null);
                            await fetchData();
                        }}
                    />
                )}

                {woRateModalEmployee && (
                    <EmployeeWorkOrderRateModal
                        isOpen={true}
                        employee={woRateModalEmployee}
                        canManage={canManageWORates}
                        onClose={() => setWoRateModalEmployee(null)}
                        onSaved={async () => {
                            setWoRateModalEmployee(null);
                            await fetchData();
                        }}
                    />
                )}

                {showPhoto && enrollmentEmployee && (
                    <EnrollmentPhotoModal
                        employee={enrollmentEmployee}
                        enrollment={enrollmentEmployee.enrollment}
                        canEdit={canEditPhoto}
                        onClose={() => setEnrollmentEmployee(null)}
                        onChanged={(next, action) => handleEnrollmentChanged(enrollmentEmployee, next, action)}
                        onOpenOther={(m) => {
                            const other = employeeByID.get(m.employeeID);
                            openPhotoModal(
                                other ?? {
                                    employeeID: m.employeeID,
                                    altEmployeeID: m.altEmployeeID,
                                    firstName: m.firstName,
                                    lastName: m.lastName,
                                }
                            );
                        }}
                        onError={(message) => showToast({ kind: "err", message })}
                    />
                )}

                {showPhoto && needingPhotosOpen && (
                    <EmployeesNeedingPhotosModal
                        rows={missingRows}
                        scopeLabel={needingPhotosScopeLabel}
                        canEdit={canEditPhoto}
                        onClose={() => setNeedingPhotosOpen(false)}
                        onOpen={(employeeID) => {
                            const emp = employeeByID.get(employeeID);
                            if (!emp) return;
                            setNeedingPhotosOpen(false);
                            openPhotoModal(emp);
                        }}
                        onToast={(kind, message) => showToast({ kind, message })}
                    />
                )}

                {/* toast */}
                {toast && (
                    <div className="emp-toast-wrap">
                        <div className={`emp-toast ${toast.kind}`}>
                            {toast.spin ? (
                                <EnmSpin light />
                            ) : toast.kind === "ok" ? (
                                <CheckCircle size={18} />
                            ) : (
                                <AlertTriangle size={18} />
                            )}
                            <span className="t-msg">{toast.message}</span>
                            {toast.actionLabel && (
                                <button
                                    className="t-undo"
                                    onClick={() => {
                                        const fn = toast.onAction;
                                        setToast(null);
                                        fn?.();
                                    }}
                                >
                                    {toast.actionLabel}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

        </PageWrapper>
    );
};

export default EmployeeView;
